// web/src/lib/mockApi.ts — believable fake data so EVERY Api method renders before any backend exists
// ("see-it-first"). CONTRACT units: timestamps are unix SECONDS, money is integer CENTS, create returns { id }.
// Small in-memory store so submit/answer/reply/save update live within the session.
import type { Api } from './api';
import { FEATURES } from './features';
import { adminConsoleMock, type ConsoleKey } from './mockAdmin';
import type {
  Me, HomeFeed, Site, SiteDetail, Build, BuildSummary, RunDetail, RunSummary, RunStatus, SupportThread, BillingState, RepoRef,
  Release, Clarification, Stage, AcRow, Message, PaymentsSummaryDto, Settings, HomeSite, OnboardingDto, TickState,
  AdminOverview, AdminCustomerRow, AdminCustomerDetail, AdminUserRow,
  AdminBuildRow, AdminBuildDetail, GateReport,
  AdminTaskRow, AdminTaskDetail,
  DnsListDto, DnsPlanDto, DnsChangeSetSummary, DnsRecordDto, DnsOp, DnsPlan, DnsDiff, DnsDiffRow,
  CreditState, TopUpPack,
  AdminPlatformMetrics, AdminCharts, AdminOrgRow, AdminOrgDetail, AdminHealth, AdminBlockedRequest,
  AdminSignals,
  MediaAsset, MediaPresign, MediaUploadPlan, MediaKind,
  MyPreview, AdminLead, PreviewLeadStatus, SiteReport, ReportCard, Proposal, NeedsAction,
} from './types';

const delay = <T>(v: T, ms = 280): Promise<T> => new Promise((r) => setTimeout(() => r(v), ms));
let seq = 100;
const id = (p: string) => `${p}_${++seq}`;
// Fixed "now" (unix seconds) so relative ages are stable in mock mode.
const NOW = Math.floor(Date.parse('2026-06-26T18:00:00Z') / 1000);
const ago = (mins: number) => NOW - mins * 60;
// REAL wall-clock seconds. The frozen NOW above keeps relative ages stable, but doc 22's publish
// progress and 7-day undo window are measured against the browser's actual clock — a frozen
// `publishStartedAt` would render every mock publish as seven weeks overdue.
const realAgo = (secs: number) => Math.floor(Date.now() / 1000) - secs;

// Identity is env-driven so you can demo BOTH access levels with no backend, and the mock honours the
// SAME rule the API enforces (D15 §3): isAdmin is true only when the signed-in email is on the admin
// allowlist. Default identity is a regular, self-signed-up EXTERNAL user (kc.x.hansen@gmail.com) — the
// admin nav/screens stay hidden. To preview the operator console set VITE_MOCK_EMAIL=kc@yourproject.ai.
//   VITE_MOCK_EMAIL        — the simulated signed-in user (default: the regular external user)
//   VITE_MOCK_ADMIN_EMAILS — mirrors the server ADMIN_EMAILS allowlist (default: kc@yourproject.ai)
const MOCK_EMAIL = (import.meta.env.VITE_MOCK_EMAIL ?? 'kc.x.hansen@gmail.com').toLowerCase();
const MOCK_ADMINS = (import.meta.env.VITE_MOCK_ADMIN_EMAILS ?? 'kc@yourproject.ai')
  .toLowerCase().split(',').map((s) => s.trim()).filter(Boolean);
const nameFromEmail = (e: string): string =>
  (e.split('@')[0] ?? e).split(/[._-]/).filter(Boolean)
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');

// Portal access (spec 13). Preview the other screens with `?mock_access=none|prospect|prospect-…` (sticks for the
// tab); unset = client (or admin for an admin email). Spec 24's prospect screen states: `prospect` (the form; sending it
// moves to "building"), `prospect-building`, `prospect-demo` (demo + buy button), `prospect-declined`, `prospect-ended`
// (demo put away), `prospect-off` (PREVIEW_FORM_ENABLED off, nothing sent yet).
const PROSPECT_MODES = ['prospect', 'prospect-building', 'prospect-demo', 'prospect-declined', 'prospect-ended', 'prospect-off'] as const;
type MockAccess = 'client' | 'none' | (typeof PROSPECT_MODES)[number];
function mockAccess(): MockAccess {
  try {
    const q = new URLSearchParams(window.location.search).get('mock_access');
    if (q) sessionStorage.setItem('ypd_mock_access', q);
    const v = sessionStorage.getItem('ypd_mock_access');
    return v === 'none' || (PROSPECT_MODES as readonly string[]).includes(v ?? '') ? (v as MockAccess) : 'client';
  } catch { return 'client'; }
}
const MOCK_IS_ADMIN = MOCK_ADMINS.includes(MOCK_EMAIL);
const ME: Me = {
  name: nameFromEmail(MOCK_EMAIL),
  email: MOCK_EMAIL,
  orgId: 'org_acme', orgName: 'Acme Studio', role: 'owner',
  isAdmin: MOCK_IS_ADMIN,
  access: 'client',
};
const meAccess = (): Me['access'] => {
  if (MOCK_IS_ADMIN) return 'admin';
  const a = mockAccess();
  return a === 'client' ? 'client' : a === 'none' ? 'none' : 'prospect';
};

// ── Update credits (12_CREDITS_UX.md Phase A) ─────────────────────────────────────────────────
// One mutable balance so the whole loop is demoable with no backend: the meter renders it, a mock
// request that settles spends from it, and submitting at zero parks the new row at
// `awaiting_credits` exactly as the API does (11_COST_GATEWAY §4.4 — the soft stop).
// The three tone thresholds of doc 12 §6 are reachable without editing this file:
//   VITE_MOCK_CREDITS_USED=780  → 78%  normal   (the default)
//   VITE_MOCK_CREDITS_USED=850  → 85%  amber
//   VITE_MOCK_CREDITS_USED=1000 → 100% danger + the parked-request path
//   VITE_MOCK_CREDITS_GRANTED=0 → the 0/0 case of §7 (100% danger, deliberately not "healthy")
//   VITE_MOCK_CREDITS_KILLED=1  → the kill-switch line (02 §5), which is NOT the cap
const mockCredits: CreditState = {
  creditsUsed: Number(import.meta.env.VITE_MOCK_CREDITS_USED ?? 780),
  creditsGranted: Number(import.meta.env.VITE_MOCK_CREDITS_GRANTED ?? 1000),
  resetsAt: ago(-5 * 24 * 60), // 5 days out, stable against the frozen NOW
  killed: import.meta.env.VITE_MOCK_CREDITS_KILLED === '1',
};
const creditsRemaining = () => Math.max(mockCredits.creditsGranted - mockCredits.creditsUsed, 0);

// ── Top-up packs (Phase B) ────────────────────────────────────────────────────────────────────
// Mirrors GET /v1/billing/packs. 02 §2's three rows, at exactly $0.15/credit.
const mockPacks: TopUpPack[] = [
  { id: 'topup_200', name: 'Small', credits: 200, priceCents: 3000 },
  { id: 'topup_500', name: 'Standard', credits: 500, priceCents: 7500 },
  { id: 'topup_1200', name: 'Large', credits: 1200, priceCents: 18000 },
];

const repo = (owner: string, name: string): RepoRef => ({ host: 'github', owner, name });
const release = (version: string, mins: number): Release => ({ id: id('rel'), version, deployedAt: ago(mins), url: 'https://example.com' });

// ── Requests (chalice update tasks, CHALICE_TASKS_SPEC v2) ─────────────────────────────────────────
// Mock tasks walk the REAL state machine (§4a) on a timer so demo mode exercises the full flow:
// queued → working → (clarifying ⇄ answer) → preview_ready → (tweak) → merging → live → rollback.
type MockRun = RunDetail & { _next?: { at: number; to: RunStatus } | null; _asked?: boolean; _agent?: boolean };
const msg = (role: Message['role'], body: string, mins: number): Message =>
  ({ id: id('msg'), role, body, createdAt: ago(mins) });
const legacy = { prUrl: null, ciResult: null as 'succeeded' | 'failed' | null, failureReason: null, stages: [] as Stage[], clarifications: [] as Clarification[], acMatrix: [] as AcRow[], killed: false };

const runs: MockRun[] = [
  {
    id: 'run_201', displayNo: 12, title: 'Add a newsletter signup to the footer', status: 'clarifying',
    createdAt: ago(95), updatedAt: ago(20), siteId: 'site_1', siteName: 'Marketing Site', costUsd: '0.42',
    ticketBody: 'We want a "Subscribe" field in the footer that signs people up to our mailing list.',
    previewUrl: null, changeSummary: null, _asked: true,
    messages: [
      msg('chalice', 'Quick question — which mailing-list provider do you use (Mailchimp, Constant Contact, something else)? I need to know where to send the signups.', 80),
    ],
    ...legacy,
  },
  {
    id: 'run_202', displayNo: 13, title: 'Fix mobile menu overlapping the hero on iPhone', status: 'working',
    createdAt: ago(60), updatedAt: ago(4), siteId: 'site_1', siteName: 'Marketing Site', costUsd: '0.31',
    ticketBody: 'On iPhone the hamburger menu sits on top of the hero text. Should push content down or overlay cleanly.',
    previewUrl: null, changeSummary: null, _asked: true,
    messages: [
      msg('chalice', "Here's what I'll change: adjust the mobile menu so it overlays cleanly above the hero on phone screens, without covering the headline.", 10),
    ],
    ...legacy,
  },
  {
    id: 'run_203', displayNo: 11, title: 'Add a pricing page with three tiers', status: 'preview_ready',
    createdAt: ago(1500), updatedAt: ago(40), siteId: 'site_2', siteName: 'Storefront', costUsd: '1.18',
    ticketBody: 'New /pricing page with Starter/Pro/Scale tiers and a monthly/annual toggle.',
    previewUrl: 'https://dev.d2mock203.amplifyapp.com', _asked: true,
    changeSummary: 'I added a new Pricing page with your three tiers — Starter, Pro, and Scale — and a monthly/annual toggle. It matches the rest of your site and works great on phones.',
    messages: [
      msg('chalice', "Here's what I'll change: add a /pricing page with your three tiers and a monthly/annual toggle.", 1400),
      msg('chalice', 'Your preview is ready — take a look and press "Make it live" when you\'re happy.', 40),
    ],
    ...legacy,
  },
  // The QA gate leg (doc 20) and the doc 11 soft stop, so demo mode can actually reach the three
  // states that used to render unlabelled (90_OPEN_ITEMS D-4 + D-12). Both deliberately carry a
  // previewUrl the screen must NOT show — that withholding is the point of doc 20.
  {
    id: 'run_206', displayNo: 15, title: 'Swap the homepage hero photo for the new team shot', status: 'verifying',
    createdAt: ago(30), updatedAt: ago(2), siteId: 'site_1', siteName: 'Marketing Site', costUsd: '0.54',
    ticketBody: 'Use the new team photo on the homepage hero instead of the stock one.',
    previewUrl: 'https://dev.d2mock206.amplifyapp.com', changeSummary: null, _asked: true,
    messages: [
      msg('chalice', "Here's what I'll change: swap the homepage hero photo for the new team shot.", 25),
    ],
    ...legacy,
  },
  {
    id: 'run_207', displayNo: 10, title: 'Add a booking form to the services page', status: 'needs_attention',
    createdAt: ago(400), updatedAt: ago(180), siteId: 'site_2', siteName: 'Storefront', costUsd: '2.06',
    ticketBody: 'We want visitors to be able to book a slot straight from the services page.',
    previewUrl: 'https://dev.d2mock207.amplifyapp.com', changeSummary: null, _asked: true,
    messages: [
      msg('system', "We're taking a closer look at this update before you see it. We'll be in touch shortly.", 180),
    ],
    ...legacy,
  },
  {
    id: 'run_209', displayNo: 14, title: 'Add the new opening hours to the contact page', status: 'merging',
    createdAt: ago(220), updatedAt: ago(1), siteId: 'site_1', siteName: 'Marketing Site', costUsd: '0.39',
    ticketBody: 'Contact page should show the new Saturday hours, 9–3.',
    previewUrl: 'https://dev.d2mock209.amplifyapp.com', _asked: true,
    changeSummary: 'Your contact page now shows the new Saturday hours.',
    // Mid-publish: the bar is drawn from these two and stops at 95% until the poller says live.
    publishStartedAt: realAgo(55), expectedPublishMs: 210_000,
    _next: { at: Date.now() + 45_000, to: 'live' },
    messages: [
      msg('chalice', 'Your preview is ready — take a look and press "Make it live" when you\'re happy.', 20),
    ],
    ...legacy,
  },
  {
    id: 'run_208', displayNo: 16, title: 'Update the footer copyright year', status: 'awaiting_credits',
    createdAt: ago(15), updatedAt: ago(15), siteId: 'site_2', siteName: 'Storefront', costUsd: '0.00',
    ticketBody: 'Footer still says 2025.',
    previewUrl: null, changeSummary: null, messages: [], _asked: false,
    ...legacy,
  },
  {
    id: 'run_204', displayNo: 9, title: 'Speed up the blog index (lazy-load images)', status: 'live',
    createdAt: ago(5000), updatedAt: ago(4300), siteId: 'site_1', siteName: 'Marketing Site', costUsd: '0.77',
    previewUrl: 'https://dev.d2mock204.amplifyapp.com', _asked: true,
    ticketBody: 'Blog index is slow on mobile — lazy-load below-the-fold images.',
    creditsCharged: 17, // settled — the chat shows "This update used 17 credits." (doc 12 §3)
    // Published 3 days ago → inside the 7-day undo window (doc 22 §2, §7 item 8).
    publishStartedAt: realAgo(3 * 86400), expectedPublishMs: 198_000, rollbackUntil: realAgo(-4 * 86400),
    changeSummary: 'Your blog now loads much faster on phones — images below the fold only load as visitors scroll to them.',
    messages: [
      msg('system', 'Your update is live 🎉', 4300),
    ],
    ...legacy,
  },
  // Portal spec 19 — the update agent's turns, so every new label and card renders in mock mode.
  {
    id: 'run_210', displayNo: 17, title: 'Add our new van photo to the About page', status: 'waiting',
    createdAt: ago(12), updatedAt: ago(12), siteId: 'site_1', siteName: 'Marketing Site',
    ticketBody: 'Please add the photo of our new van to the About page.\n\nUploaded image: https://blob.test/media/van.jpg',
    previewUrl: null, changeSummary: null, _asked: true,
    messages: [msg('system', "We're finishing another change on your site. Yours starts right after — nothing you need to do.", 12)],
    ...legacy,
  },
  {
    id: 'run_211', displayNo: 18, title: '3 photos for the site', status: 'queued_for_update',
    createdAt: ago(300), updatedAt: ago(300), siteId: 'site_2', siteName: 'Storefront',
    ticketBody: 'Photos from this week’s jobs.\n\nUploaded image: https://blob.test/media/job1.jpg\nUploaded image: https://blob.test/media/job2.jpg\nUploaded image: https://blob.test/media/job3.jpg',
    previewUrl: null, changeSummary: null, _asked: true,
    messages: [msg('system', "Thanks! We'll use these in your next monthly update (around November 19). Want them on the site sooner? Ask for a change and attach them.", 300)],
    ...legacy,
  },
  {
    id: 'run_212', displayNo: 8, title: 'Try a darker header', status: 'set_aside',
    createdAt: ago(30000), updatedAt: ago(9000), siteId: 'site_2', siteName: 'Storefront',
    ticketBody: 'Could the header be a darker blue?',
    previewUrl: null, changeSummary: null, _asked: true,
    messages: [
      msg('system', 'Your change is ready to look at.', 29000),
      msg('system', "We've set this aside so your site can keep improving. Reply here any time to pick it back up.", 9000),
    ],
    ...legacy,
  },
  {
    id: 'run_205', displayNo: 3, title: 'Change opening hours on the contact page', status: 'archived',
    createdAt: ago(60000), updatedAt: ago(59000), siteId: 'site_1', siteName: 'Marketing Site',
    ticketBody: 'Please change Saturday hours to 10-2.',
    previewUrl: null, changeSummary: null, messages: [], _asked: false, creditsCharged: 11,
    ...legacy,
  },
];
const summary = (r: RunDetail): RunSummary => ({
  id: r.id, title: r.title, status: r.status, createdAt: r.createdAt, updatedAt: r.updatedAt,
  siteId: r.siteId, siteName: r.siteName, costUsd: r.costUsd, displayNo: r.displayNo,
});

// Timer tick: apply a scheduled transition once its time passes (called on every read).
const VAGUE = (t: string) => /\bpop\b|\bbetter\b|\bnicer\b/i.test(t) || t.trim().split(/\s+/).length < 4;
function tickRun(r: MockRun): void {
  if (!r._next || Date.now() < r._next.at) return;
  const to = r._next.to;
  r._next = null;
  r.status = to;
  r.updatedAt = Math.floor(Date.now() / 1000);
  if (to === 'working') {
    if (!r._asked && VAGUE(r.ticketBody + ' ' + r.title)) {
      r._asked = true;
      r._next = { at: Date.now() + 3500, to: 'clarifying' };
    } else {
      r.messages.push(msg('chalice', "Here's what I'll change: " + r.title.toLowerCase() + '. Building it now — your preview link will appear here shortly.', 0));
      // The build's own deploy verdict is no longer the customer state (doc 20 §3): every preview
      // is gated first, so demo mode walks through `verifying` too.
      r._next = { at: Date.now() + 6000, to: 'verifying' };
    }
  } else if (to === 'verifying') {
    r.previewUrl = r.previewUrl ?? `https://dev.d2${r.id.replace(/\D/g, '')}mock.amplifyapp.com`;
    r._next = { at: Date.now() + 4000, to: 'preview_ready' }; // nothing is said to the customer here
  } else if (to === 'clarifying') {
    r.messages.push(msg('chalice', 'Quick question — can you tell me a bit more about exactly what you want changed, and where on the site?', 0));
  } else if (to === 'preview_ready') {
    r.previewUrl = r.previewUrl ?? `https://dev.d2${r.id.replace(/\D/g, '')}mock.amplifyapp.com`;
    r.changeSummary = `Done — ${r.title.replace(/\.$/, '')}. Check the preview and press "Make it live" when you're happy.`;
    r.messages.push(FEATURES.chalice
      ? msg('chalice', 'Your preview is ready — take a look and press "Make it live" when you\'re happy.', 0)
      : msg('system', 'Your change is ready to look at.', 0));
  } else if (to === 'live') {
    r.messages.push(msg('system', 'Your update is live 🎉', 0));
    // doc 22 §2: the undo is open for 7 days from the publish, not from now.
    r.rollbackUntil = (r.publishStartedAt ?? Math.floor(Date.now() / 1000)) + 7 * 24 * 60 * 60;
    // `live` is the terminal state that settles the charge (doc 11 §4.4). Charge once — a
    // rollback re-enters `merging → live` and must NOT bill the customer a second time.
    if (FEATURES.chalice && r.creditsCharged == null) {
      r.creditsCharged = 17; // 02 §3's typical simple update
      mockCredits.creditsUsed += r.creditsCharged;
    }
    agentNext(r.siteId); // the next change on this site gets its turn (portal spec 19)
  }
}

// ── The update agent's turns (portal spec 19), mock-only. The real API has no runner until spec 20 (requests wait
// `queued`); the mock pretends one exists so the whole walk is clickable: queued → preview → Make it live → live.
// Only rows made in this session (`_agent`) take part in the per-site turns; the seeds above are display-only.
const DEV_HOLD: RunStatus[] = ['queued', 'clarifying', 'preview_ready', 'publish_waiting', 'merging'];
const agentHolder = (siteId?: string, except?: string) =>
  runs.find((x) => x._agent && x.siteId === siteId && x.id !== except && DEV_HOLD.includes(x.status));
function agentStart(r: MockRun): void {
  r.status = 'queued';
  r.updatedAt = Math.floor(Date.now() / 1000);
  r.messages.push(msg('system', "Got it. We'll start on this shortly.", 0));
  r._next = { at: Date.now() + 6000, to: 'preview_ready' };
}
function agentNext(siteId?: string): void {
  // a change waiting to go live goes first, then the oldest waiting ask gets the site
  const pw = runs.find((x) => x._agent && x.siteId === siteId && x.status === 'publish_waiting');
  if (pw) { pw.status = 'merging'; pw.publishStartedAt = Math.floor(Date.now() / 1000); pw.expectedPublishMs = 210_000; pw._next = { at: Date.now() + 4500, to: 'live' }; return; }
  const next = runs.filter((x) => x._agent && x.siteId === siteId && x.status === 'waiting').sort((a, b) => a.createdAt - b.createdAt)[0];
  if (next) agentStart(next);
}
const tickAll = () => runs.forEach(tickRun);

// ── Sites (projects) ──────────────────────────────────────────────────────────────────────────────
// Registry fields (portal spec 11) shared by every mock site; each row overrides what differs.
const PREFS = (): Site['preferences'] => ({ reportEmails: [], updateMode: 'show_me', gbpManaged: null });
const reg = (slug: string, over: Partial<Site> = {}) => ({
  slug, domain: null, liveHost: null, previewUrl: null, stage: 'live' as const, takesPayments: true,
  preferences: PREFS(), planInterval: null, pausedReason: null, ...over,
});
const sites: Site[] = [
  { id: 'site_1', name: 'Marketing Site', url: 'https://acme.com', repo: repo('acme', 'marketing-site'), createdAt: ago(40000), openRequests: 2, lastRelease: release('v1.8.0', 4300), status: 'live', state: 'live', mode: 'engaged', ...reg('marketing-site', { domain: 'acme.com', liveHost: 'acme.com', planInterval: 'month' }) },
  { id: 'site_2', name: 'Storefront', url: 'https://shop.acme.com', repo: repo('acme', 'storefront'), createdAt: ago(30000), openRequests: 1, lastRelease: release('v2.1.3', 1600), status: 'live', state: 'active', mode: 'engaged', ...reg('storefront', { domain: 'shop.acme.com', liveHost: 'shop.acme.com', planInterval: 'year' }) },
  // A sold site that isn't live yet (Home shows its go-live checklist, portal spec 14). Takes payments, like portal-smoke.
  { id: 'site_3', name: 'Help Center', url: null, repo: repo('acme', 'help-center'), createdAt: ago(9000), openRequests: 0, lastRelease: null, status: 'needs_domain', state: 'needs_domain', mode: 'engaged', ...reg('help-center', { stage: 'sold', takesPayments: true }) },
  // A spec (built-but-unsold) site so the Claim affordance is exercisable in mock mode.
  { id: 'site_4', name: 'Bright Bakes (spec)', url: 'https://bright-bakes.yourproject.ai', repo: null, createdAt: ago(2000), openRequests: 0, lastRelease: null, status: 'live', state: 'spec', mode: 'spec', ...reg('bright-bakes', { stage: 'demo', takesPayments: false }) },
];

// ── Go-live checklist (portal spec 14) — stateful, and kept in sessionStorage because Checkout is a full-page
// navigation: plan todo → done after checkout(), preferences → done after setPreferences(), nameservers todo → done
// on the 2nd "Check again", adminStartGoLive() → stage going_live. Account is always done (the login is the client).
type MockOnboarding = { plan: boolean; nsDone?: boolean; stage?: Site['stage']; prefs?: Site['preferences'] };
const nsChecks: Record<string, number> = {}; // refreshes this page load (the first load is one)
const OB_KEY = 'ypd_mock_onboarding';
const obAll = (): Record<string, MockOnboarding> => { try { return JSON.parse(sessionStorage.getItem(OB_KEY) ?? '{}'); } catch { return {}; } };
const ob = (siteId: string): MockOnboarding => obAll()[siteId] ?? { plan: false };
const obSet = (siteId: string, patch: Partial<MockOnboarding>) => {
  try { sessionStorage.setItem(OB_KEY, JSON.stringify({ ...obAll(), [siteId]: { ...ob(siteId), ...patch } })); } catch { /* private mode */ }
};
for (const s of sites) { const o = obAll()[s.id]; if (o?.stage) s.stage = o.stage; if (o?.prefs) s.preferences = o.prefs; } // survive the reload
function mockOnboarding(s: Site, refresh: boolean, admin: boolean): OnboardingDto {
  if (refresh && (nsChecks[s.id] = (nsChecks[s.id] ?? 0) + 1) >= 2) obSet(s.id, { nsDone: true });
  const o = ob(s.id);
  const plan: TickState = o.plan || s.planInterval ? 'done' : 'todo';
  const stripe: TickState = s.takesPayments ? (mockConnect.chargesEnabled ? 'done' : 'todo') : 'not_needed';
  const preferences: TickState = s.preferences.reportEmails.length > 0 && s.preferences.gbpManaged !== null ? 'done' : 'todo';
  const nameservers: TickState = s.liveHost ? 'not_needed' : o.nsDone ? 'done' : 'todo';
  return {
    siteId: s.id, slug: s.slug, stage: s.stage, account: 'done', plan, stripe, preferences, nameservers,
    clientDone: plan === 'done' && stripe !== 'todo', ready: plan === 'done' && nameservers !== 'todo', paymentsReady: stripe !== 'todo',
    nameserverInfo: admin && nameservers !== 'not_needed'
      ? { nameServers: ['ana.ns.cloudflare.com', 'bob.ns.cloudflare.com'], registrar: 'GoDaddy', currentNameServers: ['ns51.domaincontrol.com', 'ns52.domaincontrol.com'] }
      : null,
    checkedAt: new Date().toISOString(),
  };
}
const homeSite = (s: Site): HomeSite => ({ id: s.id, slug: s.slug, name: s.name, stage: s.stage, liveHost: s.liveHost, stageChangedAt: s.createdAt, takesPayments: s.takesPayments });

// ── Monthly reports (portal spec 17): two months for the Marketing Site (Denali-style, update-brain 11 §4), one practice
// month for the Storefront (the "practice data" banner), none for the others (empty state / no Home card).
const safeCard = (up: string, down: string, downNote: string, days: number, speed: string): ReportCard => ({
  key: 'safe', title: 'Kept safe and fast', lines: [
    { label: 'Up and running', value: up, note: 'The whole month' },
    { label: 'Times it went down', value: down, ...(downNote ? { note: downNote } : {}) },
    { label: 'Security certificate', value: 'Good', note: `Renews in ${days} days` },
    { label: 'Speed on phones', value: speed, note: 'Good' },
    { label: 'Attacks blocked', value: '214', note: 'Mostly bots looking for logins your site doesn’t have' },
  ],
});
const mockReports: Record<string, SiteReport[]> = {
  site_1: [
    { id: 'rep_1_2026_10', period: '2026-10', windowFrom: '2026-09-21', windowTo: '2026-10-18', publishedAt: '2026-10-20T15:10:00Z', doc: {
      headline: '1,240 people visited your site in October (↑ 18%)', practiceData: false, screenshots: [],
      cards: [
        { key: 'customers', title: 'Your customers', lines: [
          { label: 'Visits', value: '1,240', delta: 'up', deltaText: '↑ 18%', note: '64% on phones · 92% from Utah' },
          { label: 'Tapped to call', value: '31', delta: 'up', deltaText: '+7' },
          { label: 'Asked for directions', value: '9', delta: 'new', deltaText: 'new' },
          { label: 'Sent the quote form', value: '6', delta: 'down', deltaText: '−2' },
          { label: 'How they found you', value: 'Google 61%', note: 'Direct 22% · Facebook 9%' },
        ] },
        { key: 'search', title: 'Found on Google and Bing', lines: [
          { label: 'Clicks from Google', value: '212', delta: 'up', deltaText: '↑ 12%' },
          { label: 'Times shown on Google', value: '8,930', delta: 'up', deltaText: '↑ 9%' },
          { label: 'Top search', value: 'roof repair near me', note: 'Also: roofer Ogden, metal roof cost' },
          { label: 'Clicks from Bing', value: 'Not measured this month', note: 'We’re still setting this up' },
        ] },
        safeCard('99.98%', '1', 'Back in 4 minutes on Oct 3', 61, '1.8 s'),
      ],
      done: ['Added 8 new job photos from your Instagram', 'Fixed the phone number in the footer', 'Made the quote form shorter on phones'],
      noticed: ['12 people opened the quote form and stopped at “budget”', 'Most people look at Roof Repair first, then Reviews'],
    } },
    { id: 'rep_1_2026_09', period: '2026-09', windowFrom: '2026-08-24', windowTo: '2026-09-20', publishedAt: '2026-09-22T15:05:00Z', doc: {
      headline: '1,050 people visited your site in September (↑ 6%)', practiceData: false, screenshots: [],
      cards: [
        { key: 'customers', title: 'Your customers', lines: [
          { label: 'Visits', value: '1,050', delta: 'up', deltaText: '↑ 6%', note: '61% on phones' },
          { label: 'Tapped to call', value: '24', delta: 'flat', deltaText: '±0' },
          { label: 'Sent the quote form', value: '8', delta: 'up', deltaText: '+3' },
        ] },
        { key: 'search', title: 'Found on Google and Bing', lines: [
          { label: 'Clicks from Google', value: '189', delta: 'up', deltaText: '↑ 4%' },
          { label: 'Times shown on Google', value: '8,190', delta: 'flat', deltaText: '±1%' },
          { label: 'Top search', value: 'roofer near me' },
        ] },
        safeCard('100%', '0', '', 92, '1.9 s'),
      ],
      done: ['Added your new service area to the contact page'],
      noticed: ['Visits on phones keep growing'],
    } },
  ],
  site_2: [
    { id: 'rep_2_2026_10', period: '2026-10', windowFrom: '2026-09-21', windowTo: '2026-10-18', publishedAt: '2026-10-20T15:12:00Z', doc: {
      headline: 'Your shop is set up and ready for customers', practiceData: true, screenshots: [],
      cards: [
        { key: 'customers', title: 'Your customers', lines: [
          { label: 'Visits', value: 'A few dozen', delta: 'new', deltaText: 'new' },
          { label: 'Orders', value: '2', delta: 'new', deltaText: 'new' },
        ] },
        safeCard('99.95%', '0', '', 74, '2.1 s'),
      ],
      done: ['Set up your online orders'],
      noticed: [],
    } },
  ],
};

// ── Monthly suggestions (portal spec 18) — in memory: answers stick for the page load (a full navigation resets them).
// Marketing Site: "show me first", two ideas waiting. Storefront: "update my site automatically", so its idea was a yes
// at release and the month's update is on its way.
const mockAutoYes = () => { // the end of the 5th weekday from today, like the API's autoYesAt (close enough for a mock)
  const d = new Date(); let left = 5;
  while (left > 0) { d.setDate(d.getDate() + 1); if (d.getDay() !== 0 && d.getDay() !== 6) left--; }
  d.setHours(23, 59, 0, 0); return d.toISOString();
};
const mockProposals: Record<string, Proposal> = {
  site_1: { id: 'prop_1_2026_10', period: '2026-10', previewUrl: 'https://update.d1mock.amplifyapp.com', status: 'published', autoYesOn: mockAutoYes(), items: [
    { id: 'pi_1', class: 'green', title: 'Fixed the phone number in the footer', why: 'It was missing a digit, so taps to call failed.', source: null, screenshotUrl: null, status: 'applied' },
    { id: 'pi_2', class: 'green', title: 'Described your job photos for people who use screen readers', why: 'Each photo now says what it shows.', source: null, screenshotUrl: null, status: 'applied' },
    { id: 'pi_3', class: 'yellow', title: 'Add a page for roof repair in Ogden', why: '31 people searched for roof repair in Ogden last month and none of them found a page about it on your site.', source: 'Google', screenshotUrl: null, status: 'pending' },
    { id: 'pi_4', class: 'yellow', title: 'Make the quote form shorter on phones', why: '12 people started the form and stopped at “budget”. Asking for it later should help.', source: null, screenshotUrl: null, status: 'pending' },
    { id: 'pi_5', class: 'red', title: 'Show prices on the services page', why: 'Visitors look for prices before they call. Whether to show them is your call.', source: null, screenshotUrl: null, status: 'discuss' },
  ] },
  site_2: { id: 'prop_2_2026_10', period: '2026-10', previewUrl: null, status: 'decided', autoYesOn: null, items: [
    { id: 'pi_6', class: 'green', title: 'Made product photos load faster', why: 'They were large files.', source: null, screenshotUrl: null, status: 'applied' },
    { id: 'pi_7', class: 'yellow', title: 'Add a “Gift cards” link to the menu', why: 'Several people searched your shop for gift cards.', source: null, screenshotUrl: null, status: 'approved' },
  ] },
};
const mockSuggestionsWaiting = (): NeedsAction[] => Object.entries(mockProposals)
  .filter(([, p]) => p.status === 'published' && p.items.some((i) => i.status === 'pending'))
  .map(([siteId, p]) => ({ kind: 'suggestions', siteId, proposalId: p.id, count: p.items.filter((i) => i.status === 'pending').length }));

// ── Website builds (intake → interview → approve → live) ─────────────────────────────────────────────
type MockBuild = {
  id: string; mode: 'engaged' | 'spec'; businessName: string; createdAt: number;
  status: Build['status']; userTurns: number; paidAt: number | null; liveSiteId?: string;
  qaDoneAt?: number; // set when the on-demand checks finish (runBuildQa) — gates the live hand-off
  // Interview resume parity with the API: turns + plan live on the run, not in component state.
  turns: { role: 'user' | 'assistant'; text: string }[]; planMd?: string;
};
const builds: MockBuild[] = [];
// Customer-run "final checks" results (QA on-demand): no summary until the owner runs them.
const qaGates = new Map<string, NonNullable<Build['gate']>>();
const BUILD_ORDER: Build['status'][] = ['queued', 'researching', 'planning', 'building', 'deploying', 'awaiting_qa', 'live'];
// The 3 customer phases + Live (doc 13). Sub-statuses + blocked statuses map to a phase via PHASE_OF.
const STAGE_LABELS: { key: 'research' | 'build' | 'finalize' | 'live'; name: string }[] = [
  { key: 'research', name: 'Research' },
  { key: 'build', name: 'Build' },
  { key: 'finalize', name: 'Finalize' },
  { key: 'live', name: 'Live' },
];
const PHASE_OF: Partial<Record<Build['status'], 'research' | 'build' | 'finalize'>> = {
  researching: 'research', planning: 'research', researched: 'research', research_blocked: 'research',
  building: 'build', deploying: 'build', built: 'build', build_blocked: 'build',
  finalizing: 'finalize', awaiting_qa: 'finalize', finalize_blocked: 'finalize',
};
function mockPhaseForView(status: Build['status']): Build['phase'] {
  if (status === 'live') return undefined;
  return PHASE_OF[status] ?? (status === 'failed' ? 'build' : undefined);
}

// Drive a paid mock build forward over wall-clock so the StageRail visibly advances on poll.
function tickBuild(b: MockBuild): void {
  if (b.paidAt == null || b.status === 'live' || b.status === 'failed') return;
  const elapsed = Date.now() - b.paidAt;
  // HOLDS at awaiting_qa like the real pipeline (doc 04): the owner runs the final checks, then it
  // goes live a few beats after the all-green report (stand-in for approve/auto-golive).
  const step = Math.min(BUILD_ORDER.length - 1, Math.floor(elapsed / 1500));
  const holdIdx = BUILD_ORDER.indexOf('awaiting_qa');
  const cleared = b.qaDoneAt != null && Date.now() - b.qaDoneAt > 6000;
  b.status = BUILD_ORDER[cleared ? step : Math.min(step, holdIdx)]!;
  if (b.status === 'live' && !b.liveSiteId) {
    const slug = b.businessName.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'site';
    const newSite: Site = {
      id: id('site'), name: b.businessName, url: `https://${slug}.yourproject.ai`, repo: null,
      createdAt: ago(0), openRequests: 0, lastRelease: null,
      status: 'live', state: b.mode === 'spec' ? 'spec' : 'active', mode: b.mode,
      ...reg(slug, { stage: b.mode === 'spec' ? 'demo' : 'live' }),
    };
    sites.unshift(newSite);
    b.liveSiteId = newSite.id;
  }
}

function buildStages(status: Build['status']): Build['stages'] {
  const broken = status === 'failed' || status.endsWith('_blocked');
  const phase = mockPhaseForView(status);
  const activeKey = status === 'live' ? 'live' : phase;
  const idx = activeKey ? STAGE_LABELS.findIndex((s) => s.key === activeKey) : -1;
  return STAGE_LABELS.map((s, i) => {
    let st: Build['stages'][number]['status'] = 'pending';
    if (idx === -1) st = 'pending'; // pre-research (intake/awaiting_approval/paid/queued)
    else if (i < idx) st = 'complete';
    else if (i === idx) st = status === 'live' ? 'complete' : broken ? 'failed' : 'running';
    return { name: s.name, status: st };
  });
}

function toBuild(b: MockBuild): Build {
  tickBuild(b);
  const blocked = b.status.endsWith('_blocked');
  return {
    id: b.id, mode: b.mode, status: b.status, stages: buildStages(b.status),
    businessName: b.businessName, interview: b.turns, agreedPlanMd: b.planMd,
    // Phased pipeline fields (doc 13) so the rail/banner render in the see-it-first mock.
    phase: mockPhaseForView(b.status),
    phaseAttempts: { research: 0, build: blocked && b.status === 'build_blocked' ? 1 : 0, finalize: 0 },
    blocked,
    terminalFailed: b.status === 'failed',
    researchMd: ['researching', 'planning', 'building', 'deploying', 'awaiting_qa', 'live'].includes(b.status)
      ? `# Research — ${b.businessName}\n\n_Mock research log. The real brain writes this from the prospect footprint + web research._\n\n- Audience and the ONE thing the site should do\n- Competitors scanned from public links\n- Pages + content plan`
      : undefined,
    // Curated about-you (00 §4e) — brand/tone/SEO/about ONLY, NO infra/pricing/stripe. Doubles
    // as the "no leak" smoke fixture (§7). Rendered as Markdown on the status screen.
    customerResearchMd: ['awaiting_qa', 'live'].includes(b.status)
      ? [
          `## Brand & Tone`,
          `Warm, trustworthy, local. Headline voice is plain-spoken, not corporate.`,
          ``,
          `## Who You Serve`,
          `Homeowners in the metro who need a job done right the first time.`,
          ``,
          `## SEO Plan (plain English)`,
          `We target "<trade> near me" and your three top services, one page each.`,
          ``,
          `## Market & Competitors`,
          `Three local competitors scanned; yours wins on reviews and response time.`,
        ].join('\n')
      : undefined,
    siteUrl: b.status === 'live' && b.liveSiteId ? sites.find((s) => s.id === b.liveSiteId)?.url ?? undefined : undefined,
    // Preview exists from the Build phase onward (doc: build-screen) — so the customer can watch it take shape.
    demoUrl: ['building', 'deploying', 'awaiting_qa', 'live'].includes(b.status)
      ? `https://${(b.businessName ?? 'your-site').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')}.yourproject.ai`
      : undefined,
    // Customer-safe Finalize checklist — only once someone actually RAN the checks (QA on-demand);
    // "no report yet" is the normal initial state at awaiting_qa now.
    gate: qaGates.get(b.id) ??
      (b.status === 'live'
        ? { mobileFriendly: true, linksWork: true, imagesReal: true, checkoutTested: true, allClear: true }
        : undefined),
    costUsd: b.status === 'live' ? '0.00' : undefined,
  };
}

// ── Support ───────────────────────────────────────────────────────────────────────────────────────
const threads: SupportThread[] = [
  {
    id: 'thr_1', subject: 'Can you connect a custom domain?', status: 'answered', lastMessageAt: ago(800),
    messages: [
      { id: 'm1', authorRole: 'customer', body: 'Is it possible to point yourdomain.com at my site here?', createdAt: ago(900) },
      { id: 'm2', authorRole: 'operator', body: 'Yes — custom domains are coming as a module. For now I can wire it manually, want me to?', createdAt: ago(800) },
    ],
  },
];

// ── Billing (subscription the customer pays us) ─────────────────────────────────────────────────────
// Website care (portal spec 15): one trialing monthly plan + one site that can start its plan.
const billing: BillingState = {
  configured: true, amounts: { month: 10000, year: 100000 },
  plans: [
    { siteId: 'site_1', siteSlug: 'marketing-site', siteName: 'Marketing Site', interval: 'month', status: 'trialing',
      trialEnd: ago(-60 * 24 * 60), currentPeriodEnd: ago(-60 * 24 * 60), cancelAtPeriodEnd: false },
  ],
  canStart: [{ siteId: 'site_3', siteName: 'Help Center' }],
  invoices: [
    { id: 'in_3', date: ago(2 * 24 * 60), amount: 10000, status: 'paid', url: '#' },
    { id: 'in_2', date: ago(32 * 24 * 60), amount: 10000, status: 'paid', url: '#' },
    { id: 'in_1', date: ago(62 * 24 * 60), amount: 10000, status: 'paid', url: '#' },
  ],
};

// ── Recent sales (admin customer detail mock only; the client's Payments screen is Stripe's embed, spec 16) ──
const mockRecentCharges = [
  { amount: 12900, when: ago(120), desc: 'Order #1043' },
  { amount: 4900, when: ago(380), desc: 'Order #1042' },
  { amount: 24900, when: ago(900), desc: 'Order #1041' },
  { amount: 7900, when: ago(1500), desc: null },
];

// ── Settings ────────────────────────────────────────────────────────────────────────────────────────
const settings: Settings = { businessName: 'Acme Studio', contactEmail: 'kc.x.hansen@gmail.com', notifyEmail: true };

// ── AI DNS tab (mirrors the server: sample zone + canned plan + stateful apply/revert) ───────────────
// A site has a manageable zone only once a CUSTOM domain is connected; the shared *.yourproject.ai
// subdomain has no separate zone (matches domain_status gating server-side).
const dnsZones: Record<string, DnsRecordDto[]> = {};
type MockDnsCs = {
  id: string; siteId: string; intent: string | null; plan: DnsPlan; diff: DnsDiff;
  destructive: boolean; snapshot: DnsRecordDto[]; status: DnsChangeSetSummary['status'];
  createdAt: number; appliedAt: number | null;
};
const dnsChangeSets: MockDnsCs[] = [];

function domainOf(site: Site | undefined): string | null {
  if (!site?.url) return null;
  const host = site.url.replace(/^https?:\/\//, '').replace(/\/.*$/, '');
  if (host.endsWith('yourproject.ai')) return null; // shared subdomain → no zone to manage
  return host;
}
function seedZone(domain: string): DnsRecordDto[] {
  let n = 1;
  const rid = () => `mrec_${domain.replace(/\W/g, '')}_${n++}`;
  return [
    { id: rid(), type: 'A', name: domain, content: '203.0.113.10', ttl: 1, proxied: true },
    { id: rid(), type: 'CNAME', name: `www.${domain}`, content: domain, ttl: 1, proxied: true },
    { id: rid(), type: 'MX', name: domain, content: 'aspmx.l.google.com', ttl: 1, proxied: false, priority: 1 },
    { id: rid(), type: 'TXT', name: domain, content: 'v=spf1 include:_spf.google.com ~all', ttl: 1, proxied: false },
    { id: rid(), type: 'TXT', name: `_dmarc.${domain}`, content: `v=DMARC1; p=none; rua=mailto:dmarc@${domain}`, ttl: 1, proxied: false },
  ];
}
function zoneFor(siteId: string, domain: string): DnsRecordDto[] {
  if (!dnsZones[siteId]) dnsZones[siteId] = seedZone(domain);
  return dnsZones[siteId];
}
function cannedDnsPlan(domain: string, current: DnsRecordDto[], intent?: string, pastedDns?: string): DnsPlan {
  const ops: DnsOp[] = [];
  const warnings: string[] = [];
  if (pastedDns && pastedDns.trim()) {
    for (const raw of pastedDns.split('\n')) {
      const line = raw.trim();
      if (!line || line.startsWith(';') || line.startsWith('#')) continue;
      const [rawName, rawType, ...rest] = line.split(/\s+/);
      if (!rawName || !rawType || rest.length === 0) continue;
      const name = rawName === '@' ? domain : rawName.endsWith(domain) ? rawName : `${rawName}.${domain}`;
      const type = rawType.toUpperCase() as DnsOp['type'];
      const content = rest.join(' ').replace(/^"|"$/g, '');
      ops.push({ action: 'create', type, name, content, ttl: 1, reason: 'Recreate from your current DNS so nothing breaks at cutover.' });
    }
    return { ops, summary: `Replicate ${ops.length} record(s) from your pasted DNS into Cloudflare.`, warnings: ['Apex NS/SOA are intentionally skipped.'] };
  }
  const apexA = current.find((r) => r.type === 'A' && r.name === domain);
  ops.push(apexA
    ? { action: 'update', type: 'A', name: domain, content: '198.51.100.20', ttl: 1, proxied: true, recordId: apexA.id, reason: 'Point your domain at your new site.' }
    : { action: 'create', type: 'A', name: domain, content: '198.51.100.20', ttl: 1, proxied: true, reason: 'Point your domain at your new site.' });
  if (!current.some((r) => r.type === 'CNAME' && r.name === `www.${domain}`)) {
    ops.push({ action: 'create', type: 'CNAME', name: `www.${domain}`, content: domain, ttl: 1, proxied: true, reason: 'Send www. to the same place as your bare domain.' });
  }
  if (current.some((r) => r.type === 'MX')) warnings.push('Your email (MX / SPF / DMARC) records are left unchanged.');
  let cfgSettings: DnsPlan['settings'];
  if (/redirect|ssl|https|too many/i.test(intent ?? '')) {
    cfgSettings = { ssl: 'full', always_use_https: true };
    warnings.push('Switches SSL mode to Full and turns on Always Use HTTPS to fix redirect loops.');
  }
  return {
    ops,
    summary: `Point ${domain} (and www) at your new site, proxied through Cloudflare, and leave your email untouched.`,
    warnings, settings: cfgSettings,
  };
}
function diffFor(current: DnsRecordDto[], plan: DnsPlan): DnsDiff {
  const match = (op: DnsOp) => current.find((r) => r.id === op.recordId) ?? current.find((r) => r.type === op.type && r.name === op.name);
  const fmt = (op: DnsOp) => `${op.priority != null ? op.priority + ' ' : ''}${op.content ?? ''}`.trim();
  const rows: DnsDiffRow[] = plan.ops.map((op) => {
    if (op.action === 'create') return { kind: 'added', type: op.type, name: op.name, after: fmt(op), proxied: op.proxied, reason: op.reason };
    const cur = match(op);
    const before = cur ? `${cur.priority != null ? cur.priority + ' ' : ''}${cur.content}` : undefined;
    if (op.action === 'update') return { kind: 'changed', type: op.type, name: op.name, before, after: fmt(op), proxied: op.proxied, reason: op.reason };
    return { kind: 'removed', type: op.type, name: op.name, before, reason: op.reason };
  });
  return { rows, settings: plan.settings };
}

// ── Admin (fee/commission lives ONLY here) ───────────────────────────────────────────────────────────
const adminCustomers: AdminCustomerRow[] = [
  { id: 'cus_a1', email: 'kc.x.hansen@gmail.com', business: 'Acme Studio', tier: 'Pro', status: 'active', mrr: 15000, connected: true, payoutsEnabled: true, incomeMonth: 712900, commissionMonth: 35645, lastLogin: ago(30), created: ago(40000) },
  { id: 'cus_a2', email: 'jordan@brightbakes.co', business: 'Bright Bakes', tier: 'Starter', status: 'active', mrr: 10000, connected: true, payoutsEnabled: true, incomeMonth: 318400, commissionMonth: 15920, lastLogin: ago(220), created: ago(22000) },
  { id: 'cus_a3', email: 'sam@northpeak.io', business: 'North Peak', tier: 'Pro', status: 'past_due', mrr: 15000, connected: false, payoutsEnabled: false, incomeMonth: 0, commissionMonth: 0, lastLogin: ago(5000), created: ago(15000) },
  { id: 'cus_a4', email: null, business: 'Untitled (no signup)', tier: '—', status: 'none', mrr: 0, connected: false, payoutsEnabled: false, incomeMonth: 0, commissionMonth: 0, lastLogin: null, created: ago(800) },
];

const adminOverview: AdminOverview = {
  mrr: adminCustomers.filter((c) => c.status === 'active').reduce((s, c) => s + c.mrr, 0),
  activeCustomers: adminCustomers.filter((c) => c.status === 'active').length,
  commissionMonth: adminCustomers.reduce((s, c) => s + c.commissionMonth, 0),
  totalMonth: 0,
  annualPlans: 2, monthlyPlans: 6, sitesLive: 5, modelCostMonthCents: 2_133, // portal spec 22 (Money)
};
adminOverview.totalMonth = adminOverview.mrr + adminOverview.commissionMonth;

// ── Master admin console (50_ADMIN_CONSOLE.md) — org-keyed money/usage/quality ────────────────
// Numbers are chosen to make the four questions in §2 legible before any backend exists:
//   • ONE org is nearly unprofitable (North Peak: $150 plan, $128 of AI) so the amber/red tint
//     and the margin-ascending default sort are both visible.
//   • ONE org is paused, ONE is unprovisioned (no org_budgets row — 90_OPEN_ITEMS D-3).
//   • ONE request is parked at needs_attention with unbilled spend, so the D-13/D-14 queue and
//     its Retry are demoable end to end.
const mockGrants: Record<string, number> = {};
const mockPaused: Record<string, boolean> = {};
type ConsoleSeed = {
  orgId: string; name: string; tier: string | null; subStatus: string; sites: number;
  mrrCents: number; aiSpendCents: number; reworkCents: number;
  creditsUsed: number; creditsGranted: number; requests: number; gated: number; gateFirstPass: number;
  needsAttention: number; killed: boolean; lastActive: number | null; stripeCustomerId: string | null;
  unprovisioned: boolean;
};
const CONSOLE_SEEDS: ConsoleSeed[] = [
  { orgId: 'org_northpeak', name: 'North Peak', tier: 'Pro', subStatus: 'active', sites: 2,
    mrrCents: 15000, aiSpendCents: 12840, reworkCents: 2610, creditsUsed: 1712, creditsGranted: 1800,
    requests: 41, gated: 38, gateFirstPass: 26, needsAttention: 1, killed: false,
    lastActive: ago(45), stripeCustomerId: 'cus_a3', unprovisioned: false },
  { orgId: 'org_brightbakes', name: 'Bright Bakes', tier: 'Starter', subStatus: 'active', sites: 1,
    mrrCents: 10000, aiSpendCents: 5120, reworkCents: 430, creditsUsed: 683, creditsGranted: 1000,
    requests: 18, gated: 17, gateFirstPass: 15, needsAttention: 0, killed: false,
    lastActive: ago(190), stripeCustomerId: 'cus_a2', unprovisioned: false },
  { orgId: 'org_acme', name: 'Acme Studio', tier: 'Pro', subStatus: 'active', sites: 3,
    mrrCents: 18000, aiSpendCents: 3960, reworkCents: 380, creditsUsed: 528, creditsGranted: 1200,
    requests: 14, gated: 13, gateFirstPass: 12, needsAttention: 0, killed: false,
    lastActive: ago(12), stripeCustomerId: 'cus_a1', unprovisioned: false },
  { orgId: 'org_tap', name: 'Corner Tap House', tier: 'Starter', subStatus: 'past_due', sites: 1,
    mrrCents: 10000, aiSpendCents: 1180, reworkCents: 940, creditsUsed: 157, creditsGranted: 1000,
    requests: 5, gated: 4, gateFirstPass: 2, needsAttention: 0, killed: true,
    lastActive: ago(3200), stripeCustomerId: null, unprovisioned: false },
  { orgId: 'org_new', name: 'Lakeside Pottery (new)', tier: null, subStatus: 'none', sites: 0,
    mrrCents: 0, aiSpendCents: 0, reworkCents: 0, creditsUsed: 0, creditsGranted: 0,
    requests: 0, gated: 0, gateFirstPass: 0, needsAttention: 0, killed: false,
    lastActive: null, stripeCustomerId: null, unprovisioned: true },
];
const pctOr = (n: number, d: number): number | null => (d ? n / d : null);
function consoleOrgs(_days: 7 | 30 | 90): AdminOrgRow[] {
  return CONSOLE_SEEDS.map((s) => {
    const creditsGranted = s.creditsGranted + (mockGrants[s.orgId] ?? 0);
    const marginCents = s.mrrCents - s.aiSpendCents;
    return {
      orgId: s.orgId, name: s.name, tier: s.tier, subStatus: s.subStatus, sites: s.sites,
      creditsUsed: s.creditsUsed, creditsGranted,
      utilisationPct: pctOr(s.creditsUsed, creditsGranted),
      mrrCents: s.mrrCents, aiSpendCents: s.aiSpendCents, reworkCents: s.reworkCents,
      marginCents, marginPct: pctOr(marginCents, s.mrrCents),
      reworkPct: pctOr(s.reworkCents, s.aiSpendCents),
      requests: s.requests, gatePassPct: pctOr(s.gateFirstPass, s.gated),
      needsAttention: s.needsAttention, killed: mockPaused[s.orgId] ?? s.killed,
      lastActive: s.lastActive, stripeCustomerId: s.stripeCustomerId, unprovisioned: s.unprovisioned,
    };
    // §3.2 — margin ASCENDING, dormant orgs (no MRR, no spend) last. See the same CASE in the
    // API's ORDER BY: a $0/$0 trial is numerically the lowest margin on the board and would
    // otherwise bury the paying customer who is actually unprofitable.
  }).sort((a, b) => {
    const dormant = (r: AdminOrgRow) => Number(r.mrrCents === 0 && r.aiSpendCents === 0);
    return dormant(a) - dormant(b) || a.marginCents - b.marginCents;
  });
}
function consolePlatform(days: 7 | 30 | 90): AdminPlatformMetrics {
  const rows = consoleOrgs(days);
  const sum = (f: (r: AdminOrgRow) => number) => rows.reduce((s, r) => s + f(r), 0);
  const mrrCents = sum((r) => (r.subStatus === 'active' ? r.mrrCents : 0));
  const aiSpendCents = sum((r) => r.aiSpendCents);
  const reworkCents = sum((r) => r.reworkCents);
  const creditsUsed = sum((r) => r.creditsUsed);
  const creditsGranted = sum((r) => r.creditsGranted);
  const requestsTotal = sum((r) => r.requests);
  const requestsComplex = Math.round(requestsTotal * 0.17);
  const gated = 72;
  const gateFirstPass = 55;
  const grossMarginCents = mrrCents - aiSpendCents;
  return {
    windowDays: days, fromIso: new Date((NOW - days * 86400) * 1000).toISOString(),
    mrrCents, aiSpendCents, reworkCents, grossMarginCents,
    marginPct: pctOr(grossMarginCents, mrrCents),
    creditsUsed, creditsGranted, utilisationPct: pctOr(creditsUsed, creditsGranted),
    reworkPct: pctOr(reworkCents, aiSpendCents),
    requestsTotal, requestsComplex, complexMixPct: pctOr(requestsComplex, requestsTotal),
    gated, gateFirstPass, gatePassPct: pctOr(gateFirstPass, gated),
    overCap: 2, needsAttention: mockBlocked.length,
    today: { live: 6, failed: 1, preview_ready: 3, working: 2 },
    queueTasks: 1, queueGate: 0,
  };
}
function consoleCharts(days: 7 | 30 | 90): AdminCharts {
  const spend: AdminCharts['spend'] = [];
  const outcomes: AdminCharts['outcomes'] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date((NOW - i * 86400) * 1000).toISOString().slice(0, 10);
    // Deterministic pseudo-variation so the bars are not a flat wall but never move on refresh.
    const w = (i * 37) % 11;
    const sonnet = 220 + w * 24;
    const opus = w > 6 ? 180 + w * 31 : 0; // the expensive tier is intermittent, which is the point
    spend.push({ day: d, byModel: { 'chalice-sonnet-4-6': sonnet, 'chalice-opus-5': opus }, totalCents: sonnet + opus });
    outcomes.push({ day: d, live: 2 + (w % 4), failed: w % 3 === 0 ? 1 : 0, needsAttention: w === 9 ? 1 : 0, other: w % 2 });
  }
  return { spend, outcomes };
}
let mockBlocked: AdminBlockedRequest[] = [
  { requestId: 'req_blocked_1', displayNo: 57, title: 'Add the winter menu page',
    orgId: 'org_northpeak', orgName: 'North Peak', siteId: 'site_np1', siteName: 'northpeak.io',
    blockedHours: 62.5, blockedSince: ago(3750), gateFailures: ['phonePass', 'deadLinks'],
    gateFixAttempts: 2, previewUrl: 'https://dev.d1abc.amplifyapp.com', unbilledCents: 214,
    blockedBehind: 0 },
];
function consoleOrgDetail(orgId: string, days: 7 | 30 | 90): AdminOrgDetail {
  const org = consoleOrgs(days).find((o) => o.orgId === orgId) ?? consoleOrgs(days)[0]!;
  const grant = mockGrants[org.orgId] ?? 0;
  return {
    org,
    cycleEnd: ago(-14 * 24 * 60),
    killedReason: org.killed ? 'Payment dispute — paused pending review' : null,
    creditsRollover: grant,
    ledger: [
      ...(grant ? [{ id: 'led_grant', reason: 'admin_grant', delta: grant, balanceAfter: org.creditsGranted - org.creditsUsed, requestId: null, createdAt: ago(1) }] : []),
      { id: 'led_1', reason: 'request', delta: -17, balanceAfter: org.creditsGranted - org.creditsUsed, requestId: 'req_1', createdAt: ago(60) },
      { id: 'led_2', reason: 'topup', delta: 500, balanceAfter: 812, requestId: null, createdAt: ago(2000) },
      { id: 'led_3', reason: 'request', delta: -34, balanceAfter: 329, requestId: 'req_2', createdAt: ago(2600) },
      { id: 'led_4', reason: 'cycle_reset', delta: 671, balanceAfter: 1000, requestId: null, createdAt: ago(43200) },
      { id: 'led_5', reason: 'plan_renewal', delta: 1000, balanceAfter: 1000, requestId: null, createdAt: ago(86400) },
    ],
    byModel: [
      { model: 'chalice-sonnet-4-6', calls: 96, cents: Math.round(org.aiSpendCents * 0.62), reworkCents: Math.round(org.reworkCents * 0.5) },
      { model: 'chalice-opus-5', calls: 21, cents: Math.round(org.aiSpendCents * 0.38), reworkCents: Math.round(org.reworkCents * 0.5) },
    ],
    requests: [
      { id: 'req_1', displayNo: 57, title: 'Add the winter menu page', status: 'needs_attention',
        siteName: 'northpeak.io', complexity: 'complex',
        complexityReason: 'New page plus nav and schema changes across four files.',
        creditsCharged: null, overCap: false, aiSpendCents: 214, reworkCents: 96,
        models: ['chalice-opus-5', 'chalice-sonnet-4-6'], gateGreen: false,
        gateFailures: ['phonePass', 'deadLinks'], createdAt: ago(3900), updatedAt: ago(3750) },
      { id: 'req_2', displayNo: 56, title: 'Swap the hero photo', status: 'live',
        siteName: 'northpeak.io', complexity: 'simple',
        complexityReason: 'Single asset swap, no layout change.',
        creditsCharged: 17, overCap: false, aiSpendCents: 122, reworkCents: 0,
        models: ['chalice-sonnet-4-6'], gateGreen: true, gateFailures: [],
        createdAt: ago(5200), updatedAt: ago(5100) },
      { id: 'req_3', displayNo: 55, title: 'Rebuild the booking flow', status: 'live',
        siteName: 'northpeak.io', complexity: 'complex',
        complexityReason: 'Multi-step form, validation and a new confirmation route.',
        creditsCharged: 200, overCap: true, aiSpendCents: 1840, reworkCents: 410,
        models: ['chalice-opus-5'], gateGreen: true, gateFailures: [],
        createdAt: ago(9000), updatedAt: ago(8800) },
    ],
    sites: [
      { id: 'site_np1', name: 'northpeak.io', url: 'https://northpeak.io', state: 'live', awsRegion: 'us-east-1', lastPublish: ago(5100) },
      { id: 'site_np2', name: 'northpeak-shop', url: null, state: 'building', awsRegion: 'us-west-2', lastPublish: null },
    ],
    stripeCustomerUrl: org.stripeCustomerId ? `https://dashboard.stripe.com/customers/${org.stripeCustomerId}` : null,
  };
}
function consoleHealth(days: 7 | 30 | 90): AdminHealth {
  return {
    needsAttention: mockBlocked,
    unbilledCents: mockBlocked.reduce((s, b) => s + b.unbilledCents, 0),
    failuresByPhase: [{ phase: 'build', count: 3 }, { phase: 'triage', count: 1 }],
    overCap: consoleOrgDetail('org_northpeak', days).requests.filter((r) => r.overCap),
    stalled: [],
    gateErrors: 2,
    unprovisionedOrgs: CONSOLE_SEEDS.filter((s) => s.unprovisioned).length,
    throttle: [
      { model: 'chalice-opus-5', calls: 214, throttled: 31, retries: 31 },
      { model: 'chalice-sonnet-4-6', calls: 1180, throttled: 4, retries: 4 },
    ],
    throttleScope:
      'API process only, since last restart — the chalice job’s Foundry 429s are not persisted anywhere queryable (01 §6).',
  };
}

// Learnings loop (chalice-v2 doc 51 §5) — "see-it-first": one cluster over the threshold (what
// the distiller would propose from), one emerging below it, request ids only (no customer prose
// exists in a signal to show). Counts scale with the window so the toggle visibly does something.
function consoleSignals(days: 7 | 30 | 90): AdminSignals {
  const k = days / 30;
  const n = (x: number) => Math.max(1, Math.round(x * k));
  return {
    clusters: [
      { failure: 'phonePass', requestKind: 'layout', siteStack: null, sites: n(34), occurrences: n(61),
        reworkCents: n(5240), examples: ['req_8f2a11d0', 'req_a91c22e4', 'req_0b3d44f1'], meetsThreshold: true },
      { failure: 'blankImages', requestKind: 'media', siteStack: null, sites: n(19), occurrences: n(23),
        reworkCents: n(1980), examples: ['req_c04e55a2', 'req_77ab90cd'], meetsThreshold: true },
      { failure: 'deadLinks', requestKind: 'page', siteStack: null, sites: Math.min(4, n(4)), occurrences: n(6),
        reworkCents: n(310), examples: ['req_e12f33b8'], meetsThreshold: false },
    ],
    totals: {
      signals: n(1412),
      byOutcome: { live: n(1290), failed: n(61), needs_attention: n(9), rejected: n(52) },
    },
    minSites: 5,
    distillEnabled: false,
    lastPrUrl: 'https://github.com/kc-hansen/brain/pull/42',
    lastPrAt: ago(11 * 24 * 60),
  };
}

// QA gate (doc 04) — "see-it-first": one all-green build + one with a seeded red (a dead link),
// so the AdminBuildGate renders both the approvable and the blocked state before the backend exists.
const GREEN_GATE: GateReport = { buildGreen: true, phonePass: true, axeCritical: 0, deadLinks: 0, checkoutSmoke: 'pass', favicon: true, blankImages: 0 };
const RED_GATE: GateReport = { buildGreen: true, phonePass: true, axeCritical: 0, deadLinks: 1, checkoutSmoke: 'skipped', favicon: true, blankImages: 2, notes: 'Dead internal link: /menu → 404; 2 blank image slots.' };
const adminBuilds: AdminBuildDetail[] = [
  { id: 'qa_green', businessName: 'Lakeside Pottery Studio', mode: 'engaged', status: 'awaiting_qa', demoUrl: 'https://lakeside-pottery-studio.yourproject.ai', repoUrl: 'https://github.com/kc-hansen/lakeside-pottery-studio', costUsd: '3.18', gateReport: GREEN_GATE },
  { id: 'qa_red', businessName: 'Corner Tap House', mode: 'spec', status: 'awaiting_qa', demoUrl: 'https://corner-tap-house.yourproject.ai', repoUrl: 'https://github.com/kc-hansen/corner-tap-house', costUsd: '2.74', gateReport: RED_GATE },
  // No report — the normal initial state now that checks are on-demand ("Run final checks").
  { id: 'qa_unrun', businessName: 'Bluebird Bakery', mode: 'engaged', status: 'awaiting_qa', demoUrl: 'https://bluebird-bakery.yourproject.ai', repoUrl: 'https://github.com/kc-hansen/bluebird-bakery', costUsd: '2.95', gateReport: null },
];
let qaRunSeq = 0; // vary the on-demand report per run so a re-run reads as a NEW report
const gateGreen = (g: GateReport | null) =>
  !!g && g.buildGreen && g.phonePass && g.axeCritical === 0 && g.deadLinks === 0 && g.checkoutSmoke !== 'fail';

// Connect Express acceptance state (doc 05) — mutable so the mock can flip not-connected → enabled.
const mockConnect = { connected: false, chargesEnabled: false, detailsSubmitted: false };

// On-demand site QA reports (CHALICE C4) — filled by siteQa a few seconds after the click.
const siteGates: Record<string, import('./types').BuildGateSummary> = {};

// ── Uploaded media (30_MEDIA_PIPELINE.md Phase A) ───────────────────────────────────────────
// Mirrors the API's `assets` table closely enough that MediaUpload.tsx cannot tell the difference:
// same statuses, same limits, same customer-safe sentences. The one thing it deliberately does NOT
// mirror is content sniffing (§7 item 3) — that reads the STORED BYTES server-side and there is no
// server here, so the check has no honest mock. It is covered by api/src/modules/media/media.spec.ts.
type MockAsset = {
  id: string; siteId: string; requestId: string | null; kind: MediaKind; status: string;
  name: string; bytes: number; polls: number;
  width?: number; height?: number; durationMs?: number; playbackUrl?: string; rejectReason?: string;
};
const mockAssets: MockAsset[] = [];

const mockAssetDto = (a: MockAsset): MediaAsset => ({
  id: a.id, kind: a.kind, status: a.status, name: a.name, bytes: a.bytes,
  width: a.width ?? null, height: a.height ?? null, durationMs: a.durationMs ?? null,
  thumbUrl: null, playbackUrl: a.playbackUrl ?? null, posterUrl: null,
  rejectReason: a.rejectReason ?? null,
});

// The §2 allowlist and limits, and the exact copy from media.rules.ts. Kept in step by hand; the
// canonical strings live server-side and the API test pins them.
const MOCK_TYPES: Record<string, { mime: string; kind: MediaKind }> = {
  jpg: { mime: 'image/jpeg', kind: 'image' }, jpeg: { mime: 'image/jpeg', kind: 'image' },
  png: { mime: 'image/png', kind: 'image' }, webp: { mime: 'image/webp', kind: 'image' },
  heic: { mime: 'image/heic', kind: 'image' }, heif: { mime: 'image/heic', kind: 'image' },
  avif: { mime: 'image/avif', kind: 'image' },
  mp4: { mime: 'video/mp4', kind: 'video' }, m4v: { mime: 'video/mp4', kind: 'video' },
  mov: { mime: 'video/quicktime', kind: 'video' }, webm: { mime: 'video/webm', kind: 'video' },
};
function mockValidate(f: { filename: string; contentType: string; bytes: number }):
  | { ok: true; kind: MediaKind; mime: string; filename: string }
  | { ok: false; filename: string; reason: string } {
  // The SPA's own copy of common/sanitize.ts's filename rules — zero-width and bidi gone, no path.
  const filename = String(f.filename ?? '')
    .normalize('NFC')
    .replace(/[­᠎​-‏‪-‮⁠-⁤⁦-⁩﻿￹-￻]/g, '')
    .replace(/[\\/:]+/g, ' ').split(/\s+/).filter((s) => s && !/^\.+$/.test(s)).join(' ')
    .replace(/^\.+/, '').slice(0, 120) || 'upload';
  const ext = (filename.match(/\.([A-Za-z0-9]{1,8})$/)?.[1] ?? '').toLowerCase();
  const t = MOCK_TYPES[ext] ?? (Object.values(MOCK_TYPES).find((x) => x.mime === f.contentType));
  if (!t) {
    return { ok: false, filename, reason: "We can't use that kind of file. Please add a photo (JPEG, PNG, WebP or HEIC) or a video (MP4, MOV or WebM)." };
  }
  if (!f.bytes) return { ok: false, filename, reason: 'That file came through empty — please try adding it again.' };
  if (t.kind === 'image' && f.bytes > 25 * 1024 * 1024) {
    return { ok: false, filename, reason: 'That image is too large — please keep photos under 25 MB.' };
  }
  if (t.kind === 'video' && f.bytes > 500 * 1024 * 1024) {
    return { ok: false, filename, reason: 'That video is too large — please keep videos under 500 MB.' };
  }
  return { ok: true, kind: t.kind, mime: t.mime, filename };
}

// ── Free preview (portal spec 24). The prospect screen per ?mock_access mode; the form's lead lives for the page load.
let mockSentLead: MyPreview['lead'] = null;
function mockPreview(): MyPreview {
  const a = mockAccess();
  const lead = (status: PreviewLeadStatus): MyPreview['lead'] => ({ id: 'lead_mock', status, businessName: 'Acme Studio', createdAt: new Date(Date.now() - 2 * 86_400_000).toISOString() });
  const demo = sites.find((s) => s.stage === 'demo');
  const base: MyPreview = { formEnabled: a !== 'prospect-off', lead: null, demo: null, demoEnded: false, buyUrl: null };
  switch (a) {
    case 'prospect-building': return { ...base, lead: lead('building') };
    case 'prospect-declined': return { ...base, lead: lead('declined') };
    case 'prospect-ended': return { ...base, lead: lead('demo_ready'), demoEnded: true };
    case 'prospect-demo': return {
      ...base, lead: lead('demo_ready'),
      demo: { name: demo?.name ?? 'Acme Studio', previewUrl: demo?.url ?? 'https://bright-bakes.yourproject.ai' },
      buyUrl: 'https://buy.stripe.com/test_mock?client_reference_id=org_acme',
    };
    case 'prospect': return { ...base, lead: mockSentLead };
    default: return base;
  }
}
const leadAt = (days: number) => new Date(Date.now() - days * 86_400_000).toISOString();
const mockLeads: AdminLead[] = [
  { l: { id: 'lead_new', email: 'dana@danasdetailing.test', businessName: 'Dana’s Detailing', phone: '(801) 555-0142', currentSiteUrl: 'https://danasdetailing.test', socials: ['https://facebook.com/danasdetailing', 'https://instagram.com/danasdetailing'], biggestIssue: 'Our site looks like 2012 and nobody books from it. Calls only come from Google Maps.', status: 'new', createdAt: leadAt(0.1), orgId: 'org_dana', siteId: null }, siteSlug: null, siteStage: null },
  { l: { id: 'lead_building', email: 'owner@peakroofing.test', businessName: 'Peak Roofing', phone: null, currentSiteUrl: null, socials: ['https://facebook.com/peakroofingutah'], biggestIssue: 'We don’t have a website at all, just a Facebook page.', status: 'building', createdAt: leadAt(2), orgId: 'org_peak', siteId: null }, siteSlug: null, siteStage: null },
  { l: { id: 'lead_ready', email: 'hello@brightbakes.test', businessName: 'Bright Bakes', phone: '801-555-0199', currentSiteUrl: 'https://brightbakes.test', socials: [], biggestIssue: 'Online orders are a pain: customers have to call to order a cake.', status: 'demo_ready', createdAt: leadAt(6), orgId: 'org_bright', siteId: 'site_4' }, siteSlug: 'bright-bakes', siteStage: 'demo' },
  { l: { id: 'lead_sold', email: 'sam@samsplumbing.test', businessName: 'Sam’s Plumbing', phone: null, currentSiteUrl: 'https://samsplumbing.test', socials: [], biggestIssue: 'The site is slow on phones and the contact form is broken.', status: 'sold', createdAt: leadAt(20), orgId: 'org_sam', siteId: 'site_sam' }, siteSlug: 'sams-plumbing', siteStage: 'sold' },
  { l: { id: 'lead_declined', email: 'x@spammy.test', businessName: 'Cheap SEO Links', phone: null, currentSiteUrl: null, socials: [], biggestIssue: 'Want to sell you backlinks for your clients, very cheap.', status: 'declined', createdAt: leadAt(9), orgId: 'org_x', siteId: null }, siteSlug: null, siteStage: null },
];

const mockBase: Omit<Api, ConsoleKey> = {
  me: () => delay({ ...ME, access: meAccess() }),
  home: () => {
    tickAll();
    return delay<HomeFeed>({
      sites: sites.filter((s) => !['offboarded', 'archived', 'demo'].includes(s.stage)).map(homeSite),
      inFlight: runs.filter((r) => !['live', 'failed', 'rejected', 'archived'].includes(r.status)).map(summary),
      // Deep links for the two customer-action states (§5 C2): a question to answer, a preview to review.
      needsAction: [
        ...mockSuggestionsWaiting(), // "Your monthly ideas are ready" (portal spec 18)
        ...runs
          .filter((r) => ['clarifying', 'preview_ready'].includes(r.status))
          .map((r) => ({ kind: r.status === 'clarifying' ? 'question' : 'preview', run: summary(r) } as const)),
      ],
    });
  },

  listSites: () => {
    const a = MOCK_IS_ADMIN ? 'client' : mockAccess();
    if (a === 'none') return Promise.reject(new Error('{"error":{"message":"no_access","statusCode":403}}'));
    if (a === 'prospect-demo') return delay(sites.filter((x) => x.stage === 'demo'));
    if (a !== 'client') return delay([]); // a prospect with no demo: nothing to list (ProspectGuard)
    return delay(sites.filter((x) => x.stage !== 'offboarded' && x.stage !== 'archived'));
  },
  getSite: (sid) => {
    const s = sites.find((x) => x.id === sid) ?? sites[0]!;
    const reqs = runs.filter((r) => r.siteId === s.id).map(summary);
    return delay<SiteDetail>({
      ...s, requests: reqs, releases: s.lastRelease ? [s.lastRelease] : [],
      ...(siteGates[s.id] ? { gate: siteGates[s.id] } : {}),
    });
  },

  acceptInvite: () => {
    sessionStorage.removeItem('ypd_mock_access'); // accepted: this login is a client now
    const sold = sites.find((x) => x.stage === 'sold') ?? sites[0]!;
    return delay({ orgId: ME.orgId, siteId: sold.id });
  },

  myPreview: () => delay(mockPreview()),
  sendPreview: (b) => {
    if (mockAccess() === 'prospect-off') return Promise.reject(new Error('{"error":{"message":"Not Found","statusCode":404}}'));
    if (!b.turnstileToken) return Promise.reject(new Error('{"error":{"message":"We couldn’t confirm you’re a person. Refresh the page and try again.","statusCode":400}}'));
    const created = !mockSentLead;
    mockSentLead ??= { id: id('lead'), status: 'new', businessName: b.businessName, createdAt: new Date().toISOString() };
    return delay({ id: mockSentLead.id, status: mockSentLead.status, created });
  },

  setPreferences: (sid, p) => {
    const s = sites.find((x) => x.id === sid);
    if (!s) return Promise.reject(new Error('site not found'));
    s.preferences = {
      ...s.preferences, ...p,
      ...(p.reportEmails ? { reportEmails: p.reportEmails.map((e) => e.toLowerCase()) } : {}),
    };
    obSet(s.id, { prefs: s.preferences });
    return delay({ ...s });
  },

  createBuild: (input) => {
    const b: MockBuild = {
      id: id('build'), mode: input.mode, businessName: input.businessName, createdAt: ago(0),
      status: input.mode === 'engaged' ? 'interviewing' : 'awaiting_approval',
      userTurns: 0, paidAt: null, turns: [],
    };
    builds.unshift(b);
    return delay({ id: b.id, status: b.status });
  },
  listBuilds: () => delay(builds.map((b) => {
    tickBuild(b);
    return { id: b.id, mode: b.mode, status: b.status, businessName: b.businessName, createdAt: b.createdAt, siteId: b.liveSiteId ?? null };
  })),
  buildInterview: (bid, message) => {
    const b = builds.find((x) => x.id === bid);
    if (!b) return delay({ reply: 'Build not found.', done: true });
    b.userTurns += 1;
    const done = b.userTurns >= 2;
    if (done) b.status = 'awaiting_approval';
    const reply = done
      ? 'Thanks — I have what I need. Review the plan below and approve to start your build.'
      : 'Got it. What is the ONE thing this site must do for a visitor, and who is it for?';
    b.turns.push({ role: 'user', text: message }, { role: 'assistant', text: reply });
    if (done) b.planMd = `# Agreed plan — ${b.businessName}\n\n**The ONE thing:** turn visitors into orders.\n\n**Pages:** Home, About, Services, Contact.`;
    return delay({ reply, done, agreedPlanMd: b.planMd });
  },
  approveBuild: (bid) => {
    const b = builds.find((x) => x.id === bid);
    if (b) { b.status = 'paid'; b.paidAt = Date.now(); }
    return delay({ url: `https://checkout.stripe.test/session?build=${bid}` });
  },
  retryBuild: (bid) => {
    // Mock resubmit: rewind a blocked/failed run to a running state (doc 13).
    const b = builds.find((x) => x.id === bid);
    const phase = b?.status === 'finalize_blocked' ? 'finalize' : b?.status === 'research_blocked' ? 'research' : 'build';
    if (b) b.status = phase === 'finalize' ? 'finalizing' : phase === 'research' ? 'researching' : 'building';
    return delay({ ok: true as const, phase });
  },
  runBuildQa: (bid) => {
    // Simulate the async on-demand checks (cf. adminRunQa below): the customer-safe summary lands a
    // few seconds later; the all-green result then hands off to live via tickBuild's cleared gate.
    const b = builds.find((x) => x.id === bid);
    if (b) {
      setTimeout(() => {
        qaGates.set(b.id, { mobileFriendly: true, linksWork: true, imagesReal: true, checkoutTested: true, allClear: true });
        b.qaDoneAt = Date.now();
      }, 4000);
    }
    return delay({ ok: true as const, status: 'awaiting_qa' });
  },
  getBuild: (bid) => {
    const b = builds.find((x) => x.id === bid) ?? builds[0];
    if (!b) return delay<Build>({ id: bid, mode: 'engaged', status: 'failed', stages: buildStages('failed') });
    return delay<Build>(toBuild(b));
  },
  claimSite: (sid) => {
    const s = sites.find((x) => x.id === sid);
    if (s) { s.state = 'active'; }
    return delay({ ok: true as const, state: s?.state ?? 'active' });
  },
  deleteSite: (sid) => {
    const i = sites.findIndex((x) => x.id === sid);
    if (i >= 0) sites.splice(i, 1);
    return delay({ ok: true as const });
  },
  // Customer "Run final checks" on a live site (CHALICE C4) — report lands after a few seconds.
  siteQa: (sid) => {
    setTimeout(() => {
      siteGates[sid] = { mobileFriendly: true, linksWork: true, imagesReal: true, allClear: true };
    }, 4000);
    return delay({ ok: true as const });
  },

  listRequests: () => { tickAll(); return delay(runs.map(summary)); },
  submitRequest: (siteId, title, body, _kind, assetIds) => {
    const site = sites.find((x) => x.id === siteId) ?? sites[0]!;
    // §8: the request cannot be submitted until every asset is `ready`. Enforced here as well as
    // in the API so mock mode cannot demonstrate a flow production would refuse.
    const staged = mockAssets.filter((a) => (assetIds ?? []).includes(a.id) && !a.requestId);
    const unfinished = staged.find((a) => a.status !== 'ready');
    if (unfinished) {
      return Promise.reject(new Error('One of your files is still uploading — give it a moment and try again.'));
    }
    // The soft stop (02 §5, doc 11 §4.4): at zero the ask is still ACCEPTED — the row is created
    // so the customer can see it — but it never reaches the queue. Mirrors create() in
    // requests.service.ts, so doc 12 §6 item 5 is exercisable end to end in mock mode.
    if (!FEATURES.chalice) {
      // Portal spec 19 — the update agent: photos wait for the next monthly update; a change takes its turn on dev.
      const now = Math.floor(Date.now() / 1000);
      const photo = _kind === 'photo';
      if (photo ? !['sold', 'finishing', 'going_live', 'live', 'paused', 'legacy'].includes(site.stage) : !['live', 'legacy'].includes(site.stage)) {
        return Promise.reject(new Error(JSON.stringify({ error: { statusCode: 409, message: photo ? "photo uploads open once you've signed up" : 'changes open up once your site is live' } })));
      }
      const r: MockRun = {
        id: id('run'), displayNo: 14 + runs.length, title, status: photo ? 'queued_for_update' : 'queued',
        createdAt: now, updatedAt: now, siteId: site.id, siteName: site.name, ticketBody: body,
        previewUrl: null, changeSummary: null, messages: [], _asked: true, _agent: !photo, ...legacy,
      };
      runs.unshift(r);
      if (photo) {
        r.messages.push(msg('system', ['sold', 'finishing', 'going_live'].includes(site.stage)
          ? "Thanks! We'll use these on your new site."
          : "Thanks! We'll use these in your next monthly update. Want them on the site sooner? Ask for a change and attach them.", 0));
      } else if (agentHolder(site.id, r.id)) {
        r.status = 'waiting';
        r.messages.push(msg('system', "We're finishing another change on your site. Yours starts right after — nothing you need to do.", 0));
      } else {
        agentStart(r);
      }
      site.openRequests += 1;
      return delay({ id: r.id, status: r.status });
    }
    const parked = creditsRemaining() <= 0;
    const r: MockRun = {
      id: id('run'), displayNo: 14 + runs.length, title, status: parked ? 'awaiting_credits' : 'queued',
      createdAt: Math.floor(Date.now() / 1000), updatedAt: Math.floor(Date.now() / 1000),
      siteId: site.id, siteName: site.name, costUsd: '0.00', ticketBody: body,
      previewUrl: null, changeSummary: null, messages: [], _asked: false,
      _next: parked ? null : { at: Date.now() + 2500, to: 'working' },
      ...legacy,
    };
    runs.unshift(r);
    site.openRequests += 1;
    for (const a of staged) a.requestId = r.id; // the request adopts the staged uploads (§4)
    return delay({ id: r.id });
  },
  getRun: (rid) => {
    const r = runs.find((x) => x.id === rid) ?? runs[0]!;
    tickAll(); // every row, so a change ahead of this one on the site can settle (portal spec 19 turns)
    return delay<RunDetail>({ ...r, messages: [...r.messages] });
  },
  // The chat (§4b): clarifying → answer re-triages; preview_ready → tweak round; else stored.
  sendMessage: (rid, body) => {
    const r = runs.find((x) => x.id === rid);
    if (r) {
      r.messages.push(msg('customer', body, 0));
      r.updatedAt = Math.floor(Date.now() / 1000);
      if (!FEATURES.chalice && (r.status === 'clarifying' || r.status === 'preview_ready')) {
        r.status = 'queued'; // portal spec 19: the agent runs again on dev with the reply
        r.changeSummary = null;
        r._next = { at: Date.now() + 5000, to: 'preview_ready' };
      } else if (!FEATURES.chalice && r.status === 'set_aside') {
        r._agent = true;
        if (agentHolder(r.siteId, r.id)) r.status = 'waiting'; else agentStart(r);
      } else if (r.status === 'clarifying') {
        r.status = 'queued';
        r._next = { at: Date.now() + 2000, to: 'working' };
      } else if (r.status === 'preview_ready') {
        r.status = 'working';
        r.changeSummary = null;
        r._next = { at: Date.now() + 5000, to: 'preview_ready' };
      }
    }
    return delay({ ok: true });
  },
  // "Make it live" (§4f): merging → (poller) → live.
  approveRequest: (rid) => {
    const r = runs.find((x) => x.id === rid);
    // Portal spec 19: one change goes live on a site at a time — another one publishing → publish_waiting.
    if (r && r.status === 'preview_ready' && !FEATURES.chalice
        && runs.some((x) => x.id !== r.id && x.siteId === r.siteId && x.status === 'merging')) {
      r.status = 'publish_waiting';
      r.updatedAt = Math.floor(Date.now() / 1000);
      r.messages.push(msg('system', 'Going live right after the change ahead of it — nothing you need to do.', 0));
      return delay({ ok: true as const, status: 'publish_waiting' as const });
    }
    if (r && r.status === 'preview_ready') {
      r.status = 'merging';
      r.updatedAt = Math.floor(Date.now() / 1000);
      // The two numbers PublishProgress is drawn from (doc 22 §4). The mock resolves faster than
      // the ETA on purpose, so the bar visibly stops at 95% rather than ever reaching 100%.
      r.publishStartedAt = Math.floor(Date.now() / 1000);
      r.expectedPublishMs = 210_000;
      r._next = { at: Date.now() + 4500, to: 'live' };
    }
    return delay({ ok: true });
  },
  // Rollback (§4f): revert the merge; main rebuilds; row lands back at live (original content).
  rollbackRequest: (rid) => {
    const r = runs.find((x) => x.id === rid);
    if (r && r.status === 'live') {
      r.status = 'merging';
      r.updatedAt = Math.floor(Date.now() / 1000);
      r.messages.push(msg('system', 'Rolling back this update…', 0));
      r.changeSummary = null;
      r._next = { at: Date.now() + 4500, to: 'live' };
      // the post-rollback "live" message reads differently — patch it after the tick fires
      setTimeout(() => {
        const last = r.messages[r.messages.length - 1];
        if (last?.role === 'system' && /live 🎉/.test(last.body)) {
          last.body = 'Rolled back — your site is exactly as it was before this update.';
        }
      }, 5000);
    }
    return delay({ ok: true });
  },
  uploadSas: (filename, _contentType) =>
    delay({ uploadUrl: `https://blob.test/upload/${encodeURIComponent(filename)}?sas=mock`, blobUrl: `https://blob.test/media/${encodeURIComponent(filename)}` }),

  // ── Media pipeline (doc 30 Phase A) ─────────────────────────────────────────────────────────
  // Stateful, and it enforces the SAME §2 rules the API does — the allowlist, 25 MB / 500 MB and
  // 20 files — using the SAME sentences. That matters more than it looks: the phone pass at
  // 390/360/430 (§7 item 8) is run in mock mode, and a refusal that fits on a phone in mock but
  // not in production would not have been tested at all.
  //
  // What the mock CANNOT do is content sniffing: the real gate reads the stored bytes server-side
  // (§7 item 3) and there is no server here. Mock mode therefore accepts a renamed .exe and the
  // real API rejects it — the divergence is deliberate and the API test covers the real path.
  presignMedia: (siteId, files) => {
    const staged = mockAssets.filter((a) => a.siteId === siteId && !a.requestId && a.status !== 'discarded');
    let budget = 20 - staged.length;
    const accepted: MediaUploadPlan[] = [];
    const rejected: { index: number; filename: string; reason: string }[] = [];
    files.forEach((f, index) => {
      const v = mockValidate(f);
      if (!v.ok) { rejected.push({ index, filename: v.filename, reason: v.reason }); return; }
      if (budget <= 0) { rejected.push({ index, filename: v.filename, reason: 'You can add up to 20 files to one update.' }); return; }
      budget -= 1;
      const assetId = id('asset');
      mockAssets.push({
        id: assetId, siteId, requestId: null, kind: v.kind, status: 'uploading',
        name: v.filename, bytes: f.bytes, polls: 0,
      });
      accepted.push({
        index, assetId, kind: v.kind, name: v.filename, contentType: v.mime,
        // The sentinel MediaUpload.tsx recognises: no real S3 in mock mode, so it simulates the
        // PUT and its progress rather than fetching a URL that does not exist.
        method: 'put', url: `mock://upload/${assetId}`,
      });
    });
    return delay<MediaPresign>({ accepted, rejected }, 200);
  },
  completeMedia: (assetId) => {
    const a = mockAssets.find((x) => x.id === assetId);
    if (!a) return delay(mockAssetDto({ id: assetId, siteId: '', requestId: null, kind: 'image', status: 'failed', name: 'unknown', bytes: 0, polls: 0, rejectReason: 'Something went wrong preparing that file. Please try adding it again.' }));
    // An image is processed synchronously (the API renders three webp derivatives in-process);
    // a video takes a beat, so the "still processing" state (§8) is demonstrable.
    if (a.kind === 'image') { a.status = 'ready'; a.width = 1600; a.height = 1067; }
    else { a.status = 'processing'; }
    return delay(mockAssetDto(a), 600);
  },
  getMediaAsset: (assetId) => {
    const a = mockAssets.find((x) => x.id === assetId);
    if (!a) return delay(mockAssetDto({ id: assetId, siteId: '', requestId: null, kind: 'image', status: 'failed', name: 'unknown', bytes: 0, polls: 0 }));
    if (a.status === 'processing') {
      a.polls += 1;
      if (a.polls >= 2) {
        a.status = 'ready';
        a.durationMs = 42_000;
        a.playbackUrl = `https://iframe.videodelivery.net/${a.id}`;
      }
    }
    return delay(mockAssetDto(a), 400);
  },
  deleteMedia: (assetId) => {
    const i = mockAssets.findIndex((x) => x.id === assetId);
    if (i >= 0) mockAssets.splice(i, 1);
    return delay({ ok: true as const }, 150);
  },
  listStagedMedia: (siteId) =>
    delay(mockAssets.filter((a) => a.siteId === siteId && !a.requestId).map(mockAssetDto)),
  listRequestMedia: (requestId) =>
    delay(mockAssets.filter((a) => a.requestId === requestId).map(mockAssetDto)),

  // Portal spec 16: their money only (no fee). Follows the stateful Connect mock below.
  paymentsSummary: () => delay<PaymentsSummaryDto>(mockConnect.connected
    ? { connected: true, chargesEnabled: mockConnect.chargesEnabled, detailsSubmitted: mockConnect.detailsSubmitted, incomeMonth: mockConnect.chargesEnabled ? 712900 : 0 }
    : { connected: false, chargesEnabled: false, detailsSubmitted: false, incomeMonth: null }),
  // Connect Express (doc 05) — stateful: starts not-connected (button state); once the customer
  // "starts onboarding" (connectSession), status flips to charges-enabled (badge state) so both
  // states are demonstrable in mock. The real embed needs a live publishable key + secret.
  connectSession: async () => { mockConnect.connected = true; mockConnect.chargesEnabled = true; mockConnect.detailsSubmitted = true; return { clientSecret: 'cs_test_mock' }; },
  connectStatus: () => delay({ connected: mockConnect.connected, detailsSubmitted: mockConnect.detailsSubmitted, chargesEnabled: mockConnect.chargesEnabled, payoutsEnabled: mockConnect.chargesEnabled }),
  listReports: (siteId) => delay(mockReports[siteId] ?? []),
  currentProposal: (siteId) => delay(mockProposals[siteId] ? structuredClone(mockProposals[siteId]) : null),
  decideItem: (proposalId, itemId, decision) => {
    const p = Object.values(mockProposals).find((x) => x.id === proposalId);
    const item = p?.items.find((i) => i.id === itemId);
    if (!p || !item) return Promise.reject(new Error('{"error":{"message":"suggestion not found","statusCode":404}}'));
    if (p.status !== 'published' || item.class !== 'yellow' || item.status !== 'pending') {
      return Promise.reject(new Error('{"error":{"message":"already answered or not an open idea","statusCode":409}}'));
    }
    item.status = decision === 'approve' ? 'approved' : 'declined';
    if (!p.items.some((i) => i.status === 'pending')) { p.status = 'decided'; p.previewUrl = null; p.autoYesOn = null; }
    return delay({ ok: true as const, proposalStatus: p.status });
  },
  // Go-live checklist (portal spec 14) — see mockOnboarding above.
  onboarding: (siteId, refresh) => {
    const s = sites.find((x) => x.id === siteId);
    return s ? delay(mockOnboarding(s, !!refresh, false)) : Promise.reject(new Error('{"error":{"message":"site not found","statusCode":404}}'));
  },
  setAlerts: () => delay({ ok: true as const }),
  testAlert: () => delay({ sent: true as const }),
  portal: () => delay({ url: 'https://billing.stripe.test/portal' }),
  // Mock "Checkout": the card is on file at once, back on Home like Stripe's success_url (spec 15).
  checkout: (siteId, interval) => { obSet(siteId, { plan: true }); return delay({ url: `/?plan=started&site=${siteId}&interval=${interval}` }); },
  getBilling: () => delay({ ...billing }),
  // EXACTLY the API's four fields — no cost, no tokens, no model names (doc 12 §5, 02 §2).
  getCredits: () => delay<CreditState>({ ...mockCredits }),
  listTopUpPacks: () => delay(mockPacks.map((p) => ({ ...p }))),
  // Mock top-up: stands in for Checkout → webhook → grant() → the D-5 release, all of it, so the
  // whole Phase B loop is demoable with no Stripe and no backend. It returns an IN-APP url rather
  // than a checkout.stripe.test one (as `checkout`/`portal` do) because a top-up is the one flow
  // whose POINT is what happens after you come back: the meter has to visibly rise and the parked
  // request has to visibly start. Sending the demo off-site would hide both.
  startTopUpCheckout: (packId) => {
    const pack = mockPacks.find((p) => p.id === packId) ?? mockPacks[0]!;
    // grant(): purchased credits raise the balance (02 §9 — they roll over; the meter shows one
    // number either way, doc 12 §7).
    mockCredits.creditsGranted += pack.credits;
    // …then the release (doc 11 §8, closing D-5): oldest first, and AT MOST ONE PER SITE, because
    // `dev` is one branch and releasing two parked rows for one site would reproduce exactly the
    // clobbering doc 22 §8 fixed. The rest stay parked until that site's slot frees.
    const busy = new Set(
      runs.filter((r) => ['queued', 'working', 'clarifying', 'verifying', 'preview_ready', 'merging', 'needs_attention'].includes(r.status))
        .map((r) => r.siteId ?? ''),
    );
    for (const r of [...runs].filter((x) => x.status === 'awaiting_credits').sort((a, b) => a.createdAt - b.createdAt)) {
      const site = r.siteId ?? '';
      if (busy.has(site)) continue;
      busy.add(site);
      r.status = 'queued';
      r.updatedAt = Math.floor(Date.now() / 1000);
      r._next = { at: Date.now() + 2500, to: 'working' };
      r.messages = [...r.messages, msg('system', 'Credits added — we’ve started this update.', 0)];
    }
    return delay({ url: '/billing?topup=done' });
  },

  listThreads: () => delay([...threads]),
  getThread: (tid) => delay({ ...(threads.find((t) => t.id === tid) ?? threads[0]!) }),
  postQuestion: (subject, body) => {
    const t: SupportThread = {
      id: id('thr'), subject, status: 'open', lastMessageAt: ago(0),
      messages: [{ id: id('m'), authorRole: 'customer', body, createdAt: ago(0) }],
    };
    threads.unshift(t);
    return delay({ threadId: t.id });
  },
  replyThread: (tid, body) => {
    const t = threads.find((x) => x.id === tid);
    t?.messages?.push({ id: id('m'), authorRole: 'customer', body, createdAt: ago(0) });
    if (t) t.lastMessageAt = ago(0);
    return delay({ ok: true });
  },

  getSettings: () => delay({ ...settings }),
  saveSettings: (p) => { Object.assign(settings, p); return delay({ ok: true }); },

  // ── AI DNS tab ──────────────────────────────────────────────────────────────
  getDns: (siteId) => {
    const site = sites.find((s) => s.id === siteId);
    const domain = domainOf(site);
    if (!domain) return delay<DnsListDto>({ domain: '', zoneActive: false, records: [] });
    return delay<DnsListDto>({ domain, zoneActive: true, records: zoneFor(siteId, domain).map((r) => ({ ...r })) });
  },
  planDns: (siteId, body) => {
    const site = sites.find((s) => s.id === siteId);
    const domain = domainOf(site);
    if (!domain) return Promise.reject(new Error('Connect a custom domain to this site first.'));
    const current = zoneFor(siteId, domain);
    const plan = cannedDnsPlan(domain, current, body.intent, body.pastedDns);
    const diff = diffFor(current, plan);
    const destructive = plan.ops.some((o) => o.action === 'delete');
    const cs: MockDnsCs = {
      id: id('dcs'), siteId, intent: body.intent ?? null, plan, diff, destructive,
      snapshot: current.map((r) => ({ ...r })), status: 'proposed', createdAt: ago(0), appliedAt: null,
    };
    dnsChangeSets.unshift(cs);
    return delay<DnsPlanDto>({ changeSetId: cs.id, plan, diff, destructive });
  },
  applyDns: (siteId, csId, confirmed) => {
    const cs = dnsChangeSets.find((c) => c.id === csId && c.siteId === siteId);
    if (!cs) return Promise.reject(new Error('change set not found'));
    if (cs.status !== 'proposed') return Promise.reject(new Error(`change set is ${cs.status}`));
    if (cs.destructive && !confirmed) return Promise.reject(new Error('destructive change needs confirmation'));
    const domain = domainOf(sites.find((s) => s.id === siteId))!;
    const recs = zoneFor(siteId, domain);
    for (const op of cs.plan.ops) {
      if (op.action === 'create') {
        recs.push({ id: id('mrec'), type: op.type, name: op.name, content: op.content ?? '', ttl: op.ttl ?? 1, proxied: !!op.proxied, ...(op.priority != null ? { priority: op.priority } : {}) });
      } else if (op.action === 'update') {
        const r = recs.find((x) => x.id === op.recordId) ?? recs.find((x) => x.type === op.type && x.name === op.name);
        if (r) { r.content = op.content ?? r.content; if (op.proxied != null) r.proxied = op.proxied; if (op.ttl != null) r.ttl = op.ttl; }
      } else {
        const i = recs.findIndex((x) => x.id === op.recordId) ?? -1;
        const j = i >= 0 ? i : recs.findIndex((x) => x.type === op.type && x.name === op.name);
        if (j >= 0) recs.splice(j, 1);
      }
    }
    cs.status = 'applied'; cs.appliedAt = ago(0);
    return delay({ ok: true as const });
  },
  rejectDns: (siteId, csId) => {
    const cs = dnsChangeSets.find((c) => c.id === csId && c.siteId === siteId);
    if (cs && cs.status === 'proposed') cs.status = 'rejected';
    return delay({ ok: true as const });
  },
  revertDns: (siteId, csId) => {
    const cs = dnsChangeSets.find((c) => c.id === csId && c.siteId === siteId);
    if (!cs) return Promise.reject(new Error('change set not found'));
    dnsZones[siteId] = cs.snapshot.map((r) => ({ ...r }));
    return delay({ ok: true as const });
  },
  dnsHistory: (siteId) => delay<DnsChangeSetSummary[]>(
    dnsChangeSets.filter((c) => c.siteId === siteId).map((c) => ({
      id: c.id, status: c.status, summary: c.plan.summary, intent: c.intent,
      opCount: c.plan.ops.length, hasSnapshot: c.snapshot.length > 0, createdAt: c.createdAt, appliedAt: c.appliedAt,
    })),
  ),

  adminOverview: () => delay({ ...adminOverview }),
  adminCustomers: () => delay([...adminCustomers]),
  adminCustomer: (cid) => {
    const c = adminCustomers.find((x) => x.id === cid) ?? adminCustomers[0]!;
    return delay<AdminCustomerDetail>({
      customer: c,
      invoices: billing.invoices,
      recentCharges: mockRecentCharges,
      requests: runs.slice(0, 3).map((r, i) => ({ id: 40 + i, title: r.title, state: ['live', 'archived', 'rejected'].includes(r.status) ? 'closed' : 'open', url: r.prUrl ?? 'https://github.com/acme/marketing-site/issues/1' })),
      links: {
        stripeCustomer: `https://dashboard.stripe.com/customers/${c.id}`,
        stripeConnect: c.connected ? `https://dashboard.stripe.com/connect/accounts/acct_${c.id}` : null,
      },
    });
  },
  adminOnboarding: (slug, refresh) => {
    const s = sites.find((x) => x.slug === slug);
    return s ? delay(mockOnboarding(s, !!refresh, true)) : Promise.reject(new Error(`{"error":{"message":"site '${slug}' not found","statusCode":404}}`));
  },
  adminStartGoLive: (slug, force) => {
    const s = sites.find((x) => x.slug === slug);
    if (!s) return Promise.reject(new Error(`{"error":{"message":"site '${slug}' not found","statusCode":404}}`));
    const o = mockOnboarding(s, false, true);
    if (s.stage !== 'sold' && s.stage !== 'finishing') return Promise.reject(new Error(`{"error":{"message":"site is ${s.stage}; Start go-live needs sold or finishing","statusCode":409}}`));
    if (!o.ready && !force) return Promise.reject(new Error('{"error":{"message":"not ready","statusCode":409}}'));
    s.stage = 'going_live';
    obSet(s.id, { stage: 'going_live' });
    return delay({ taskId: id('task'), stage: 'going_live' as const });
  },
  adminLeads: (status) => delay(mockLeads.filter((x) => !status || x.l.status === status).map((x) => ({ ...x, l: { ...x.l } }))),
  adminLeadStatus: (lid, status) => {
    const x = mockLeads.find((m) => m.l.id === lid);
    if (!x) return Promise.reject(new Error('{"error":{"message":"lead not found","statusCode":404}}'));
    const from: Record<string, PreviewLeadStatus[]> = { building: ['new'], new: ['building'], declined: ['new', 'building', 'demo_ready'] };
    if (!from[status]!.includes(x.l.status)) return Promise.reject(new Error(`{"error":{"message":"lead is ${x.l.status}; it can't move to ${status}","statusCode":409}}`));
    x.l.status = status;
    return delay({ id: lid, status });
  },
  adminAttachDemo: (lid, b) => {
    const x = mockLeads.find((m) => m.l.id === lid);
    if (!x) return Promise.reject(new Error('{"error":{"message":"lead not found","statusCode":404}}'));
    if (!['new', 'building', 'demo_ready'].includes(x.l.status)) return Promise.reject(new Error(`{"error":{"message":"lead is ${x.l.status}, not open","statusCode":409}}`));
    const taken = sites.find((s) => s.slug === b.slug);
    if (taken && taken.stage !== 'demo') return Promise.reject(new Error(`{"error":{"message":"site '${b.slug}' is ${taken.stage}, not a demo","statusCode":409}}`));
    x.l.status = 'demo_ready'; x.l.siteId = taken?.id ?? id('site'); x.siteSlug = b.slug; x.siteStage = 'demo';
    return delay({ ok: true as const, siteId: x.l.siteId, outboxId: id('outbox') });
  },
  adminUsers: () => delay<AdminUserRow[]>(
    adminCustomers.map((c) => ({ email: c.email, business: c.business, tier: c.tier, status: c.status, lastLogin: c.lastLogin })),
  ),
  adminBuilds: () => delay<AdminBuildRow[]>(
    adminBuilds.map((b) => ({ id: b.id, businessName: b.businessName, mode: b.mode, createdAt: ago(120), gateGreen: gateGreen(b.gateReport) })),
  ),
  adminBuild: (bid) => {
    const b = adminBuilds.find((x) => x.id === bid) ?? adminBuilds[0]!;
    return delay<AdminBuildDetail>({ ...b });
  },
  adminApproveBuild: (bid) => {
    const b = adminBuilds.find((x) => x.id === bid);
    if (b) b.status = 'live';
    return delay({ ok: true as const, status: 'live' });
  },
  adminRunQa: (bid) => {
    const b = adminBuilds.find((x) => x.id === bid);
    // Simulate the async gate: the report lands a few seconds later, so the gate screen's polling
    // ("checks running…" → checklist appears) is demoable end-to-end without a backend.
    if (b) setTimeout(() => { b.gateReport = { ...GREEN_GATE, notes: `On-demand check #${++qaRunSeq}` }; }, 4000);
    return delay({ ok: true as const, status: 'awaiting_qa' });
  },
  adminRejectBuild: (bid, _reason, requeue) => {
    const b = adminBuilds.find((x) => x.id === bid);
    if (b) b.status = 'failed';
    return delay({ ok: true as const, status: 'failed', requeuedId: requeue ? id('build') : undefined });
  },

  // ── Admin — chalice tasks (CHALICE §4g). Backed by the same mock runs so the walkthrough
  //    (customer screens + admin visibility) stays consistent in demo mode. ──────────────────
  adminTasks: () => {
    tickAll();
    return delay(runs.map((r): AdminTaskRow => ({
      id: r.id, displayNo: r.displayNo ?? null, org: 'Acme Studio', site: r.siteName ?? null,
      title: r.title, status: r.status, phase: r.previewUrl ? 'build' : 'triage',
      phaseAttempts: { triage: 0, build: r.status === 'failed' ? 2 : 0 },
      costUsd: r.costUsd, error: r.status === 'failed' ? 'self-check still failing after fix pass' : undefined,
      previewUrl: r.previewUrl ?? undefined,
      compareUrl: 'https://github.com/kc-hansen/acme/compare/main...dev',
      createdAt: r.createdAt, updatedAt: r.updatedAt,
    })));
  },
  adminTasksSummary: () => {
    const byStatus: Record<string, number> = {};
    runs.forEach((r) => { byStatus[r.status] = (byStatus[r.status] ?? 0) + 1; });
    return delay({ byStatus, tasksThisMonth: runs.length, costUsdThisMonth: '4.86', failureRate: 0.05 });
  },
  adminTask: (tid) => {
    const r = runs.find((x) => x.id === tid) ?? runs[0]!;
    return delay<AdminTaskDetail>({
      ...summary(r), body: r.ticketBody, planMd: '# Plan\n\n- do the thing', previewUrl: r.previewUrl,
      changeSummary: r.changeSummary, mergeSha: r.status === 'live' ? 'abc1234def' : null,
      costUsd: r.costUsd, error: null, phaseAttempts: { triage: 0, build: 0 },
      ghIssueUrl: null, messages: [...r.messages],
    });
  },
  adminRetryTask: (tid) => {
    const r = runs.find((x) => x.id === tid);
    if (r && r.status === 'failed') { r.status = 'queued'; r._next = { at: Date.now() + 2000, to: 'working' }; }
    return delay({ ok: true as const, phase: 'build' });
  },
  adminRollbackTask: (tid) => {
    const r = runs.find((x) => x.id === tid);
    if (r && r.status === 'live') { r.status = 'merging'; r._next = { at: Date.now() + 4000, to: 'live' }; }
    return delay({ ok: true as const });
  },

  // ── Master admin console (50_ADMIN_CONSOLE.md). Mutable so the three actions are demoable. ──
  adminMetrics: (days) => delay(consolePlatform(days)),
  adminCharts: (days) => delay(consoleCharts(days)),
  adminOrgs: (days) => delay(consoleOrgs(days)),
  adminOrg: (orgId, days) => delay(consoleOrgDetail(orgId, days)),
  adminHealth: (days) => delay(consoleHealth(days)),
  adminSignals: (days) => delay(consoleSignals(days)),
  adminGrantCredits: (orgId, credits) => {
    const o = consoleOrgs(30).find((x) => x.orgId === orgId);
    if (o) mockGrants[orgId] = (mockGrants[orgId] ?? 0) + credits;
    return delay({ ok: true as const, creditsRemaining: (o?.creditsGranted ?? 0) + credits - (o?.creditsUsed ?? 0) });
  },
  adminSetPaused: (orgId, paused) => {
    mockPaused[orgId] = paused;
    return delay({ ok: true as const, killed: paused });
  },
  adminRetryRequest: (rid) => {
    // The D-14 release: clearing the hold is what un-blocks every new update for that site.
    mockBlocked = mockBlocked.filter((b) => b.requestId !== rid);
    return delay({ ok: true as const, phase: 'build' });
  },
};

// The admin console (portal spec 22) lives in mockAdmin.ts; its go-live mock falls back to the client sites above.
export const mockApi: Api = { ...mockBase, ...adminConsoleMock(mockBase) };
